/**
 * Neo Collection - GraphQL-powered collection page
 * Handles: Product fetching, filtering, sorting, rendering, infinite scroll, cache, URL params
*/
// @ts-nocheck
// @ts-ignore

/*
  ┌─────────────────────────────────────────────────────────────────────────┐
  │ Configuration and Initial State                                         │
  └─────────────────────────────────────────────────────────────────────────┘
*/

const config = (typeof globalThis !== 'undefined' && globalThis.neoCollectionConfig) ? globalThis.neoCollectionConfig : {};

const MOBILE_WIDTH = 1024; // change if needed
const isMobile = window.innerWidthCached < MOBILE_WIDTH;

const state = {
  firstLoad: true,
  allProducts: [],
  filters: [],
  activeFilters: {},
  priceRange: { min: null, max: null, currentMin: null, currentMax: null },
  inGridSections: [],
  pagination: {
    cursor: null,
    hasNextPage: false,
    isLoading: false,
    totalLoaded: 0
  },
  sortOrder: config.sortOrder || 'manual',
  currentSort: { sortValue: null, sortKey: null, reverse: false },
  queryParams: {},
  cache: {},
  totalRenderedProducts: config.productsRenderedCount || 0,
  filterDescriptions: {},
  insertedGridSections: new Set(),
  coverageGender: 'Women',
  filtersContext: null
};

/*
  ┌──────────────────────────────────────────────────────────────────────────────────────────┐
  │ Global collection store: same reference as state. Updated on fetch and load more.        │
  └──────────────────────────────────────────────────────────────────────────────────────────┘
*/
if (typeof globalThis !== 'undefined') {
  globalThis.neoCollectionStore = state;
}

const ACCORDION_ARROW_SVG = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
  <rect width="24" height="24" rx="12" transform="matrix(-4.37114e-08 1 1 4.37114e-08 1.04907e-06 0)" fill="black" fill-opacity="0.01"/>
  <line y1="-0.428571" x2="4.73552" y2="-0.428571" transform="matrix(-0.689877 0.723927 0.590343 0.807152 15.3477 10.2852)" stroke="white" stroke-width="0.857143"/>
  <line y1="-0.428571" x2="4.6595" y2="-0.428571" transform="matrix(0.753163 0.657835 -0.519994 0.85417 8.57227 10.6484)" stroke="white" stroke-width="0.857143"/>
</svg>`;

const NEO_PLAY_ICON_SVG = `<svg class="neo-product-card-1-watch-play-icon" width="7" height="8" viewBox="0 0 7 8" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M0 7.08734V0.735669C0 0.149096 0.653735 -0.200773 1.14179 0.124599L5.90983 3.30329C6.36248 3.60505 6.36248 4.27019 5.90983 4.57195L1.18526 7.72167C0.67862 8.05943 0 7.69624 0 7.08734Z" fill="white"/>
  <defs><clipPath id="bgblur_0_6163_3766_clip_path" transform="translate(9.14844 9.14844)"><path d="M0 7.08734V0.735669C0 0.149096 0.653735 -0.200773 1.14179 0.124599L5.90983 3.30329C6.36248 3.60505 6.36248 4.27019 5.90983 4.57195L1.18526 7.72167C0.67862 8.05943 0 7.69624 0 7.08734Z"/></clipPath></defs>
</svg>`;

/*
  ┌─────────────────────────────────────────────────────────────────────────┐
  │ URL Parameters Parsing                                                  │
  └─────────────────────────────────────────────────────────────────────────┘
*/

function parseURLFilters() {
  const urlParams = new URLSearchParams(globalThis.location.search);
  const parsedFilters = {};
  let hasPriceFilter = false;
  let priceMin = null;
  let priceMax = null;

  state.queryParams = {};
  urlParams.forEach((value, key) => {
    state.queryParams[key] = value;
  });

  urlParams.forEach((value, key) => {
    if (!value && key !== 'sort_by') return;
    if (key === 'sort_by' && value) {
      state.currentSort.sortValue = value;
      const mapped = mapSortValue(value);
      state.currentSort.sortKey = mapped.sortKey;
      state.currentSort.reverse = mapped.reverse;
      return;
    }
    if (key === 'filter.v.price.gte') {
      priceMin = Number.parseFloat(value);
      hasPriceFilter = true;
      return;
    }
    if (key === 'filter.v.price.lte') {
      priceMax = Number.parseFloat(value);
      hasPriceFilter = true;
      return;
    }
    if (key.startsWith('filter.')) {
      const filterId = key;
      const decodedValue = decodeURIComponent(String(value).replace(/\+/g, ' '));
      if (!parsedFilters[filterId]) parsedFilters[filterId] = [];
      const values = decodedValue.split(',').map(v => v.trim()).filter(Boolean);
      parsedFilters[filterId].push(...values);
    }
  });

  if (hasPriceFilter) {
    // BUG 7 FIX: if min/max bounds haven't been loaded from the API yet,
    // use the URL values themselves as temporary bounds so the GraphQL price
    // filter (which requires baseMin != null) can fire on the first fetch.
    // renderFilters() will correct min/max to real API values later.
    state.priceRange.currentMin = priceMin ?? null;
    state.priceRange.currentMax = priceMax ?? null;
    state.priceRange.currentMin = priceMin ?? state.priceRange.min;
    state.priceRange.currentMax = priceMax ?? state.priceRange.max;
  }

  state.activeFilters = parsedFilters;
  return Object.keys(parsedFilters).length > 0 || hasPriceFilter;
}

/*
  ┌─────────────────────────────────────────────────────────────────────────┐
  │ Read price from neo-price-range component (DOM)                         │
  └─────────────────────────────────────────────────────────────────────────┘
*/

/*
  ┌─────────────────────────────────────────────────────────────────────────┐
  │ Build GraphQL filters from state                                        │
  └─────────────────────────────────────────────────────────────────────────┘
*/

function buildGraphQLFiltersFromActiveFilters() {
  const graphqlFilters = [];

  Object.entries(state.activeFilters).forEach(([filterId, values]) => {
    values.forEach(value => {
      const filter = state.filters.find(f => f.id === filterId);
      if (filter) {
        const filterValue = filter.values.find(v => v.label === value);
        if (filterValue?.input) {
          try {
            graphqlFilters.push(JSON.parse(filterValue.input));
          } catch (e) {
            console.error('Error parsing filter input:', e);
          }
        } else if (filterId.startsWith('filter.p.m.custom.')) {
          const metafieldKey = filterId.replace('filter.p.m.custom.', '');
          graphqlFilters.push({
            productMetafield: { namespace: 'custom', key: metafieldKey, value: value }
          });
        } else if (filterId.startsWith('filter.v.option.')) {
          const optionName = filterId.replace('filter.v.option.', '');
          graphqlFilters.push({ variantOption: { name: optionName, value: value } });
        }
      } else if (filterId.startsWith('filter.p.m.custom.')) {
        const metafieldKey = filterId.replace('filter.p.m.custom.', '');
        graphqlFilters.push({
          productMetafield: { namespace: 'custom', key: metafieldKey, value: value }
        });
      } else if (filterId.startsWith('filter.v.option.')) {
        const optionName = filterId.replace('filter.v.option.', '');
        graphqlFilters.push({ variantOption: { name: optionName, value: value } });
      }
    });
  });

  // Explicit price range filter when user has narrowed the slider
  if (state.priceRange) {
    const baseMin = state.priceRange.min;
    const baseMax = state.priceRange.max;
    const min = state.priceRange.currentMin ?? baseMin;
    const max = state.priceRange.currentMax ?? baseMax;

    const hasUserPrice =
      (state.priceRange.currentMin != null && state.priceRange.currentMin > baseMin) ||
      (state.priceRange.currentMax != null && state.priceRange.currentMax < baseMax);

    if (
      baseMin != null &&
      baseMax != null &&
      min != null &&
      max != null &&
      max >= min &&
      hasUserPrice
    ) {
      graphqlFilters.push({ price: { min, max } });
    }
  }

  return graphqlFilters;
}

/*
  ┌─────────────────────────────────────────────────────────────────────────┐
  │ Cache Management                                                        │
  └─────────────────────────────────────────────────────────────────────────┘
*/

function generateCacheKey() {
  const sortedFilters = Object.entries(state.activeFilters).sort(([a], [b]) => a.localeCompare(b)).map(([filterId, values]) => `${filterId}:${[...values].sort((a, b) => a.localeCompare(b)).join(',')}`).join('|');
  const priceKey = (state.priceRange.currentMin > state.priceRange.min || state.priceRange.currentMax < state.priceRange.max) ? `price:${state.priceRange.currentMin}-${state.priceRange.currentMax}` : '';
  const sortKey = state.currentSort.sortValue ? `sort:${state.currentSort.sortValue}` : '';
  const keyParts = [sortedFilters, priceKey, sortKey].filter(Boolean);
  return keyParts.length > 0 ? keyParts.join('|') : 'no-filters';
}

function getCachedProducts() {
  const cacheKey = generateCacheKey();
  return state.cache[cacheKey] || null;
}

function setCachedProducts(result) {
  const cacheKey = generateCacheKey();
  state.cache[cacheKey] = {
    products: result.products,
    filters: result.filters,
    pageInfo: result.pageInfo,
    timestamp: Date.now()
  };
}

/*
  ┌─────────────────────────────────────────────────────────────────────────┐
  │ URL Query Parameters Update                                             │
  └─────────────────────────────────────────────────────────────────────────┘
*/

function buildQueryString() {
  const queryParts = [];
  const nonFilterParams = [];
  const encodeValue = (value) => String(value).replace(/ /g, '+');
  const addParam = (key, value) => {
    if (value !== null && value !== undefined) queryParts.push(key + '=' + encodeValue(value));
  };

  Object.entries(state.queryParams).forEach(([key, value]) => {
    if (!key.startsWith('filter.') && key !== 'filter.v.price.gte' && key !== 'filter.v.price.lte' && key !== 'sort_by') {
      nonFilterParams.push({ key, value });
    }
  });

  Object.entries(state.activeFilters).forEach(([filterId, values]) => {
    if (values.length > 0) values.forEach(value => addParam(filterId, value));
  });

  if (
    state.priceRange &&
    state.priceRange.currentMin != null &&
    state.priceRange.currentMax != null &&
    (state.priceRange.currentMin > state.priceRange.min ||
      state.priceRange.currentMax < state.priceRange.max)
  ) {
    addParam('filter.v.price.gte', state.priceRange.currentMin.toString());
    addParam('filter.v.price.lte', state.priceRange.currentMax.toString());
  }

  if (state.currentSort.sortValue) addParam('sort_by', state.currentSort.sortValue);
  nonFilterParams.forEach(({ key, value }) => addParam(key, value));
  return queryParts.join('&');
}

function updateURLQueryParams() {
  const queryString = buildQueryString();
  const newURL = queryString ? `${globalThis.location.pathname}?${queryString}` : globalThis.location.pathname;
  globalThis.history.replaceState({}, '', newURL);
  const urlParams = new URLSearchParams(queryString);
  state.queryParams = {};
  urlParams.forEach((value, key) => { state.queryParams[key] = value; });
}

/*
  ┌─────────────────────────────────────────────────────────────────────────┐
  │ Utility                                                                 │
  └─────────────────────────────────────────────────────────────────────────┘
*/

function hasActiveFilters() {
  return Object.keys(state.activeFilters).length > 0;
}

/*
  ┌─────────────────────────────────────────────────────────────────────────┐
  │ Product count (breadcrumb)                                               │
  └─────────────────────────────────────────────────────────────────────────┘
*/

function getFiltersContext() {
  const active = JSON.stringify(state.activeFilters);
  const price = state.priceRange ? `${state.priceRange.currentMin}-${state.priceRange.currentMax}` : '';
  return `${active}|${price}`;
}

function syncFiltersContext() {
  state.filtersContext = getFiltersContext();
}

function setProductCount(reset = false, count) {
  const filteredCount = calculateFilteredProductCount();
  if (filteredCount === null) {
    reset = true;
  } else {
    count = filteredCount;
  }
  const productCountElements = document.querySelectorAll('[data-id="product-count"]');
  if (productCountElements.length === 0) return;

  productCountElements.forEach(productCountElement => {
    const defaultCount = productCountElement.dataset.default || '0';

    if (reset || count === null || count === undefined || count === 0) {
      productCountElement.textContent = `${defaultCount} Results`;
      if (productCountElement.nextElementSibling) {
        productCountElement.nextElementSibling.style.display = '';
      }
      return;
    }

    if (count != null) {
      productCountElement.textContent = `${count} of ${defaultCount} Results`;
      if (productCountElement.nextElementSibling) {
        productCountElement.nextElementSibling.style.display = 'none';
      }
    }
  })
}

/**
 * Calculates the filtered product count based on active filters.
 * - When all filtered results are loaded (no next page), returns exact state.allProducts.length.
 * - When filter counts in state came from a request with the same selection (filtersContext match),
 *   returns the minimum count among selected values.
 * - When state.filters may be stale (different selection), returns null to avoid wrong counts.
 */
function calculateFilteredProductCount() {
  if (!state.filters || state.filters.length === 0) {
    return null;
  }

  const hasPriceFilter =
    state.priceRange &&
    (state.priceRange.currentMin > state.priceRange.min || state.priceRange.currentMax < state.priceRange.max);

  if (!hasActiveFilters() && !hasPriceFilter) {
    return null;
  }

  if (!state.pagination.hasNextPage && state.allProducts.length > 0) {
    return state.allProducts.length;
  }

  if (state.filtersContext !== getFiltersContext()) {
    return null;
  }

  const selectedCounts = [];

  if (hasActiveFilters()) {
    Object.entries(state.activeFilters).forEach(([filterId, values]) => {
      const filter = state.filters.find(f => f.id === filterId);
      if (filter && filter.values) {
        values.forEach(value => {
          const filterValue = filter.values.find(v => v.label === value);
          if (filterValue && typeof filterValue.count === 'number') {
            selectedCounts.push(filterValue.count);
          }
        });
      }
    });
  }

  if (!hasActiveFilters() && hasPriceFilter) {
    const priceFilter = state.filters.find(f => f.id === 'filter.v.price');
    if (priceFilter && priceFilter.values && priceFilter.values.length > 0) {
      const priceValue = priceFilter.values[0];
      if (priceValue && typeof priceValue.count === 'number') {
        selectedCounts.push(priceValue.count);
      }
    }
  }

  if (selectedCounts.length === 0) {
    return null;
  }

  return Math.min(...selectedCounts);
}

/*
  ┌─────────────────────────────────────────────────────────────────────────┐
  │ GraphQL: product fields for neo-product-card-1 (media, metafields)      │
  └─────────────────────────────────────────────────────────────────────────┘
*/

const PRODUCT_FIELDS = `
  title
  handle
  id
  availableForSale
  productType
  tags
  priceRange {
    maxVariantPrice { amount }
    minVariantPrice { amount }
  }
  compareAtPriceRange {
    minVariantPrice { amount }
  }
  variants(first: 1) {
    edges {
      node {
        id
        price { amount }
        compareAtPrice { amount }
      }
    }
  }
  featuredImage {
    url
    altText
    width
    height
  }
  images(first: 5) {
    nodes {
      url
      altText
      width
      height
    }
  }
  media(first: 20) {
    edges {
      node {
        mediaContentType
        ... on MediaImage {
          image {
            url
            altText
            width
            height
          }
        }
        ... on Video {
          sources {
            url
            mimeType
          }
          previewImage {
            url
          }
        }
      }
    }
  }
  options(first: 3) {
    name
    values
  }
  metafield_tag: metafield(namespace: "neo", key: "usage_tag") {
    value
  }
  metafield_highlights: metafield(namespace: "neo", key: "highlights") {
    value
  }
  metafield_watch_gif: metafield(namespace: "neo", key: "watch_gif") {
    reference {
      ... on MediaImage {
        image { url }
      }
    }
  }
  metafield_color_variants: metafield(namespace: "custom", key: "color_variants") {
    references(first: 10) {
      nodes {
        ... on Product {
          id
          availableForSale
          metafield_color_swatch_image: metafield(namespace: "custom", key: "color_swatch_image") {
            reference {
              ... on MediaImage {
                image { url }
              }
            }
          }
        }
      }
    }
  }
`;

const IN_GRID_METAOBJECT_FIELDS = `
  id
  handle
  type
  fields {
    key
    value
    reference {
      ... on MediaImage {
        image {
          url
          width
          height
        }
      }
    }
  }
`;

const IN_GRID_CHOOSE_THE_GRAR_FIELDS = `
  id
  handle
  type
  fields {
    key
    value
    references(first: 20) {
      nodes {
        ... on MediaImage {
          id
          image {
            url
            width
            height
          }
        }
      }
    }
  }
`;

const IN_GRID_VIDEO_FIELDS = `
  id
  handle
  type
  fields {
    key
    reference {
      ... on Video {
        previewImage {
          width
          src
          height
        }
        mediaContentType
        sources {
          format
          height
          mimeType
          url
          width
        }
      }
    }
    value
  }
`;

const IN_GRID_FEATURED_PRODUCTS_FIELDS = `
  id
  handle
  type
  fields {
    key
    value
    reference {
      ... on MediaImage {
        image {
          url
          width
          height
        }
      }
    }
    references(first: 20) {
      nodes {
        ... on Product {
          ${PRODUCT_FIELDS}
        }
      }
    }
  }
`;

const IN_GRID_FIND_MY_FIT_FIELDS = `
  id
  handle
  type
  fields {
      key
      value
      reference {
          ... on MediaImage {
              image {
                  url
                  width
                  height
              }
          }
      }
  }
`;

const IN_GRID_IMAGE_COMPARISON_FIELDS = `

        id
        handle
        type
        fields {
            key
            value
            references(first: 4) {
                nodes {
                    ... on Collection {
                        id
                        title
                        handle
                        collection_comapre_image: metafield(key: "collection_comapre_image" namespace: "neo") {
                            value
                            references(first: 3) {
                                nodes {
                                    ... on MediaImage {
                                        image {
                                            src
                                            width
                                            height
                                            altText
                                        }
                                    }
                                }
                            }
                        }
                        collection_compare_tab: metafield(key: "collection_compare_tab" namespace: "neo") {
                            value
                        }
                        collection_compare_title: metafield(key: "collection_compare_title" namespace: "neo") {
                            value
                        }
                    }
                }
            }
        }

`

function buildCollectionQuery(collectionHandle, cursor = null, filters = [], sortKey = null, reverse = false, isInitialLoad = false) {
  const afterParam = cursor ? `, after: "${cursor}"` : '';
  const filtersParam = filters.length > 0 ? `, filters: [${filters.map(f => JSON.stringify(f).replace(/"([^"]+)":/g, '$1:')).join(', ')}]` : '';
  const sortKeyParam = sortKey ? `, sortKey: ${sortKey}` : '';
  const reverseParam = sortKey && reverse ? `, reverse: true` : '';

  return `query CollectionByHandle {
    collectionByHandle(handle: "${collectionHandle}") {
      products(first: ${config.productsPerPage || 12}${afterParam}${filtersParam}${sortKeyParam}${reverseParam}) {
        edges {
            node {
                ${PRODUCT_FIELDS}
            }
        }
        filters {
          id
          label
          type
          values {
            count
            id
            input
            label
          }
        }
        pageInfo {
            endCursor
            hasNextPage
            hasPreviousPage
            startCursor
        }
      }
      title
    }
    ${isInitialLoad ? `
      filter_value_description: metaobjects(type: "filter_value_description", first: 250) {
        nodes {
          filter_label: field(key: "filter_label") { value }
          filter_value: field(key: "filter_value") { value }
          filter_description: field(key: "filter_description") { value }
        }
      }
      inGridCategory: metaobject(handle: { handle: "${config.collectionHandle}", type: "in_grid_smart_filter_category" }) {
        ${IN_GRID_METAOBJECT_FIELDS}
      }
      inGridColor: metaobject(handle: { handle: "${config.collectionHandle}", type: "in_grid_smart_filter_color" }) {
        ${IN_GRID_METAOBJECT_FIELDS}
      }
      inGridPrice: metaobject(handle: { handle: "${config.collectionHandle}", type: "in_grid_smart_filter_price" }) {
        ${IN_GRID_METAOBJECT_FIELDS}
      }
      inGridSize: metaobject(handle: { handle: "${config.collectionHandle}", type: "in_grid_smart_filter_size" }) {
        ${IN_GRID_METAOBJECT_FIELDS}
      }
      inGridActivity: metaobject(handle: { handle: "${config.collectionHandle}", type: "in_grid_smart_filter_activity" }) {
        ${IN_GRID_METAOBJECT_FIELDS}
      }
      inGridCoverage: metaobject(handle: { handle: "${config.collectionHandle}", type: "in_grid_smart_filter_coverage" }) {
        ${IN_GRID_METAOBJECT_FIELDS}
      }
      inGridInnovation: metaobject(handle: { handle: "${config.collectionHandle}", type: "in_grid_smart_filter_innovation" }) {
        ${IN_GRID_METAOBJECT_FIELDS}
      }
      inGridStraps: metaobject(handle: { handle: "${config.collectionHandle}", type: "in_grid_smart_filter_goggle_straps" }) {
        ${IN_GRID_METAOBJECT_FIELDS}
      }
      inGridChooseTheGear: metaobject(handle: { handle: "${config.collectionHandle}", type: "in_grid_choose_the_grid" }) {
        ${IN_GRID_CHOOSE_THE_GRAR_FIELDS}
      }
      inGridVideo: metaobject(handle: { handle: "${config.collectionHandle}", type: "in_grid_video" }) {
        ${IN_GRID_VIDEO_FIELDS}
      }
      inGridFeaturedProducts: metaobject(handle: { handle: "${config.collectionHandle}", type: "in_grid_featured_products" }) {
        ${IN_GRID_FEATURED_PRODUCTS_FIELDS}
      }
      inGridFindMyFit: metaobject(handle: { handle: "${config.collectionHandle}", type: "in_grid_find_my_fit" }) {
        ${IN_GRID_FIND_MY_FIT_FIELDS}
      }
      inGridImageComparison: metaobject(handle: { handle: "${config.collectionHandle}", type: "in_grid_collection_comparision" }) {
        ${IN_GRID_IMAGE_COMPARISON_FIELDS}
      }
    ` : ''}
  }`;
}

/*
  ┌─────────────────────────────────────────────────────────────────────────┐
  │ Destructure Image Comparison Collection Data                            │
  └─────────────────────────────────────────────────────────────────────────┘
*/

function destructureImageComparisonCollectionData(node) {
  const data = {};

  node.fields.forEach(field => {
    if (field.references?.nodes?.length) {
      const nodes = field.references.nodes;
      // For collection_on_left / collections_on_right, flatten each collection's
      // collection_comapre_image into an array of image URLs
      if (field.key === 'collection_on_left' || field.key === 'collections_on_right') {
        data[field.key] = nodes.map(col => {
          const collection = { ...col };
          const cmp = collection.collection_comapre_image;

          // Flatten images to array of URLs
          if (cmp?.references?.nodes?.length && cmp.references.nodes[0]?.image?.src) {
            const imageUrls = cmp.references.nodes.map(n => n.image.src);

            // If we also have tab labels, build a label->imageUrl map
            const tabsValue = collection.collection_compare_tab?.value;
            if (tabsValue) {
              try {
                const labels = JSON.parse(tabsValue);
                if (Array.isArray(labels) && labels.length === imageUrls.length) {
                  const byLabel = {};
                  labels.forEach((label, idx) => {
                    byLabel[label] = imageUrls[idx];
                  });
                  collection.collection_comapre_image = byLabel;
                } else {
                  collection.collection_comapre_image = imageUrls;
                }
              } catch (e) {
                collection.collection_comapre_image = imageUrls;
              }
            } else {
              collection.collection_comapre_image = imageUrls;
            }
          }

          // We no longer need collection_compare_tab on the collection object
          delete collection.collection_compare_tab;

          return collection;
        });
        return;
      }

      data[field.key] = nodes;
      return;
    }

    // Skip collection_compare_tab at the top level; it's only used to
    // build the image mapping above.
    if (field.key === 'collection_compare_tab') return;

    data[field.key] = field.value;
  });

  const obj = {
    id: node.id,
    type: 'in_grid_image_comparison',
    handle: node.handle,
    // Expose positions at the top level so getSectionRowPosition works
    section_position_desktop: data.section_position_desktop,
    section_position_mobile: data.section_position_mobile,
    data
  };
  state.inGridSections.push(obj);
}

async function fetchCollectionData(cursor = null, filters = [], sortKey = null, reverse = false, isInitialLoad = false) {
  const query = buildCollectionQuery(config.collectionHandle, cursor, filters, sortKey, reverse, isInitialLoad);
  const requestOptions = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': window.storefrontAccessToken
    },
    body: JSON.stringify({ query })
  };

  const response = await fetch('/api/2023-04/graphql.json', requestOptions);
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  const data = await response.json();
  if (data.errors) {
    console.error('GraphQL errors:', data.errors);
    throw new Error('GraphQL query failed');
  }

  const collection = data.data.collectionByHandle;
  const products = collection.products.edges.map(edge => edge.node);
  const filtersData = collection.products.filters;
  const pageInfo = collection.products.pageInfo;

  if (data.data.filter_value_description) {
    state.filterDescriptions = {};
    data.data.filter_value_description.nodes.forEach((node) => {
      const label = node.filter_label?.value;
      const value = node.filter_value?.value;
      const desc = node.filter_description?.value;
      if (label && value && desc) {
        state.filterDescriptions[`${label.trim().toLowerCase()}:${value.trim().toLowerCase()}`] = desc;
      }
    });
  }

  const gridObjects = [
    data.data.inGridCategory,
    data.data.inGridColor,
    data.data.inGridPrice,
    data.data.inGridSize,
    data.data.inGridActivity,
    data.data.inGridCoverage,
    data.data.inGridInnovation,
    data.data.inGridStraps,
    data.data.inGridChooseTheGear,
    data.data.inGridVideo,
    data.data.inGridFeaturedProducts,
    data.data.inGridFindMyFit,
    data.data.inGridImageComparison
  ];

  if (isInitialLoad && state.inGridSections.length === 0) {
    state.inGridSections = [];
    gridObjects.forEach(node => {
      if (!node || !node.fields) return;

      if (node.type === 'in_grid_collection_comparision') {
        destructureImageComparisonCollectionData(node);
        return;
      }

      if (!node.fields) return;

      const obj = { id: node.id, type: node.type };

      node.fields.forEach(field => {

        // 🖼️ CASE 1 — list of images (desktop_images, mobile_images, etc)
        if (field.references?.nodes?.length) {
          const nodes = field.references.nodes;
          // If this is product references
          if (nodes[0]?.id && nodes[0]?.title) {
            obj[field.key] = nodes;
            return;
          }
          // If image references
          if (nodes[0]?.image?.url) {
            obj[field.key] = nodes.map(n => n.image.url);
            return;
          }
          obj[field.key] = nodes;
          return;
        }

        // 🎥 CASE 2 — single video reference (desktop_video, mobile_video) — full object (previewImage, mediaContentType, sources)
        if (field.reference?.sources?.length) {
          obj[field.key] = field.reference;
          return;
        }

        // 🖼️ CASE 3 — single image reference (old behavior)
        if (field.reference?.image?.url) {
          obj[field.key] = field.reference.image.url;
          return;
        }

        // 🧾 CASE 4 — normal text / json fields
        obj[field.key] = field.value;
      });

      state.inGridSections.push(obj);
    });
  }

  return { products, filters: filtersData, pageInfo, title: collection.title };
}

/*
  ┌─────────────────────────────────────────────────────────────────────────┐
  │ Filter rendering (neo-filters-container)                                │
  └─────────────────────────────────────────────────────────────────────────┘
*/

function getFilterImageUrl(filterLabel, valueLabel) {
  return getFilterImageUrlCandidates(filterLabel, valueLabel)[0];
}

function getFilterImageUrlCandidates(filterLabel, valueLabel) {
  const labelSlug = (filterLabel + '-' + valueLabel).trim().toLowerCase().replaceAll(/[^a-z0-9]+/g, '-').replaceAll(/-+/g, '-').replaceAll(/^-|-$/g, '');
  const preferredGender = (config.collectionGender || 'default').toLowerCase();
  const genderFallbackOrder = [preferredGender, 'men', 'women', 'default'].filter((gender, index, arr) => arr.indexOf(gender) === index);

  let cdnBaseUrl = 'https://cdn.shopify.com/s/files/1/0655/9629/3317/files/';
  if (filterLabel === 'Colors') {
    cdnBaseUrl = 'https://cdn.shopify.com/s/files/1/0764/2808/3500/files/';
  } else {
    cdnBaseUrl = 'https://cdn.shopify.com/s/files/1/0655/9629/3317/files/';
  }
  return genderFallbackOrder.map((gender) => {
    return `${cdnBaseUrl}filter-image-${labelSlug}-${gender}.webp?width=100`
  });
}

// Filter images can fail in bursts (e.g. a whole gender/CDN prefix missing), which
// would otherwise fire this handler once per <img> and force a style recalc each
// time. Collect the ones that need hiding and flush them in a single rAF batch.
const pendingHiddenFilterImages = new Set();
let pendingFilterImageHideFrame = null;

function flushHiddenFilterImages() {
  pendingFilterImageHideFrame = null;
  pendingHiddenFilterImages.forEach((imgEl) => {
    imgEl.style.display = 'none';
  });
  pendingHiddenFilterImages.clear();
}

function handleFilterImageError(imgEl) {
  if (!imgEl) return;

  const encodedFilterLabel = imgEl.dataset.filterLabel;
  const encodedValueLabel = imgEl.dataset.valueLabel;
  if (!encodedFilterLabel || !encodedValueLabel) {
    imgEl.onerror = null;
    return;
  }

  const filterLabel = decodeURIComponent(encodedFilterLabel);
  const valueLabel = decodeURIComponent(encodedValueLabel);
  const fallbackUrls = getFilterImageUrlCandidates(filterLabel, valueLabel);
  const currentAttempt = Number.parseInt(imgEl.dataset.imageAttempt || '0', 10);
  const nextAttempt = currentAttempt + 1;

  if (nextAttempt < fallbackUrls.length) {
    imgEl.dataset.imageAttempt = String(nextAttempt);
    imgEl.src = fallbackUrls[nextAttempt];
    return;
  }

  const isColors = filterLabel.trim().toLowerCase() === 'colors';

  // No more fallbacks left.
  // Keep broken image for Colors; hide for other filter labels.
  if (!isColors) {
    pendingHiddenFilterImages.add(imgEl);
    if (!pendingFilterImageHideFrame) {
      pendingFilterImageHideFrame = requestAnimationFrame(flushHiddenFilterImages);
    }
  }

  imgEl.onerror = null;
}

// First render: open the first accordion; after that preserve last-open state when re-rendering (e.g. after applying filters)
let isRenderingFiltersOnLoad = true;


function renderFilters(filters) {
  const container = document.querySelector('.neo-filters-container');
  if (!container) return;

  // Preserve which accordions are currently open before re-rendering (so last open stays open after applying a filter)
  const openAccordions = new Set();
  document.querySelectorAll('.neo-filters-accordion.active').forEach((accordion) => {
    const id = accordion.getAttribute('data-accordion-id');
    if (id) openAccordions.add(id);
  });

  state.filters = filters || state.filters;
  const filtersToRender = state.filters;

  const html = filtersToRender.map((filter, index) => {
    const isPrice = filter.id === 'filter.v.price';
    const isSize = filter.id === 'filter.v.option.size';
    const isCategory = filter.id === 'filter.p.product_type';
    const isColor = filter.id === 'filter.p.m.custom.colors_filter';
    const isFabric = filter.id === 'filter.p.m.custom.fabric_filter';
    const isActivity = filter.id === 'filter.p.m.neo.activity_fitler';
    const isInnovation = filter.id === 'filter.p.m.neo.innovation_filter';
    const isFlexibility = filter.id === 'filter.p.m.neo.flexibility_filter';
    const isFitType = filter.id === 'filter.p.m.custom.fit_type_filter';
    const isGender = filter.id === 'filter.p.m.custom.gender_filter';
    const isLens = filter.id === 'filter.p.m.custom.lens_filter';
    const isUsps = filter.id === 'filter.p.m.neo.usp_filter';
    const isGooglesStrap = filter.id === 'filter.p.m.custom.straps_filter';
    const isCoverage = filter.id === 'filter.p.m.neo.coverage_filter';
    const accordionId = filter.id.split('.').join('-');

    // On first page load open the first accordion; otherwise keep previously open accordions
    const wasOpen = openAccordions.has(accordionId) || (isRenderingFiltersOnLoad && index === 0);
    const accordionActiveClass = wasOpen ? ' active' : '';
    const ariaExpanded = wasOpen ? 'true' : 'false';

    const activeValuesForFilter = Array.isArray(state.activeFilters[filter.id]) ? state.activeFilters[filter.id] : [];
    const activeCount = activeValuesForFilter.length;

    let filterContent = '';

    if (isPrice) {
      const priceVal = filter.values && filter.values[0];
      let minP = state.priceRange.min;
      let maxP = state.priceRange.max;

      if (priceVal && priceVal.input && (minP === null || maxP === null)) {
        try {
          const inputData = JSON.parse(priceVal.input);
          if (inputData.price) {
            minP = inputData.price.min ?? minP;
            maxP = inputData.price.max ?? maxP;
            state.priceRange.min = minP;
            state.priceRange.max = maxP;
          }
        } catch (e) {
          console.error("Error parsing price input", e);
        }
      }

      if (state.priceRange.currentMin == null) state.priceRange.currentMin = minP;
      if (state.priceRange.currentMax == null) state.priceRange.currentMax = maxP;

      const initialMin = state.priceRange.currentMin != null ? state.priceRange.currentMin : minP;
      const initialMax = state.priceRange.currentMax != null ? state.priceRange.currentMax : maxP;

      filterContent = `<neo-price-range min="${minP}" max="${maxP}" min-value="${initialMin}" max-value="${initialMax}"></neo-price-range>`;
    } else if (isSize || isCategory || isGooglesStrap) {
      const gridClass = isSize ? 'neo-filters-grid size-flex-filter-item scroll-left hide-scrollbar' : 'neo-filters-grid hide-scrollbar';
      const columns = isSize ? 6 : 3;

      const valuesHtml = (filter.values || []).map((value) => {
        const active = state.activeFilters[filter.id] && state.activeFilters[filter.id].includes(value.label);
        const disabled = value.count === 0;
        const activeClass = active ? 'active' : '';
        const disabledClass = disabled ? 'disabled' : '';

        const encodedFilterLabel = encodeURIComponent(filter.label);
        const encodedValueLabel = encodeURIComponent(value.label);
        const imgDisplay = !isSize
          ? `<img class="neo-filters-grid-item-image" src="${getFilterImageUrl(filter.label, value.label)}" alt="${value.label}" width="100%" height="100%" data-filter-label="${encodedFilterLabel}" data-value-label="${encodedValueLabel}" data-image-attempt="0" onerror="handleFilterImageError(this)">`
          : '';

        const descriptionKey = `${filter.label.trim()}:${value.label.trim()}`;
        const description = state.filterDescriptions[descriptionKey] || '';

        return `
          <div class="neo-filters-item neo-filters-grid-item ${activeClass} ${disabledClass}" data-filter-id="${filter.id}" data-filter-value="${value.label}" ${disabled ? 'data-disabled="true"' : ''} role="button" tabindex="0">
            <span class="neo-filters-clear-filter-icon glass-effect relative">
              <svg width="6" height="6" viewBox="0 0 6 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M0.707031 -3.09053e-08L2.85352 2.14648L5 -2.18557e-07L5.70703 0.707031L3.56055 2.85352L5.70703 5L5 5.70703L2.85352 3.56055L0.707031 5.70703L-3.09035e-08 5L2.14648 2.85352L-2.18557e-07 0.707031L0.707031 -3.09053e-08Z" fill="white"/>
              </svg>
            </span>
            ${imgDisplay}
            <p class="neo-filters-grid-item-text ${isSize ? "size-text" : ""}">${value.label}</p>
            ${description ? `<p class="neo-filters-grid-item-description" style="font-size: 10px; opacity: 0.7; margin-top: 2px;">${description}</p>` : ''}
          </div>
        `;
      }).join('');


      filterContent = `
        <div class="${gridClass} ${isSize ? "size-flex-filter-item" : ""}" " style="--columns: ${columns}; --grid-gap: 15px;">
          ${valuesHtml}
        </div>
      `;
    } else {
      if (isFabric) {
        filterContent = setFabricFilter(filter);
      } else if (isCoverage) {
        filterContent = setCoverageFilter(filter);
      } else {
        const flexClass = `neo-filters-flex neo-filters-${filter.label.toLowerCase()} hide-scrollbar`;
        const valuesHtml = (filter.values || []).map((value) => {
          const active = state.activeFilters[filter.id] && state.activeFilters[filter.id].includes(value.label);
          const disabled = value.count === 0;
          const activeClass = active ? 'active' : '';
          const disabledClass = disabled ? 'disabled' : '';

          const encodedFilterLabel = encodeURIComponent(filter.label);
          const encodedValueLabel = encodeURIComponent(value.label);
          const imgDisplay = isColor || isActivity || isInnovation || isFlexibility || isFitType || isGender || isLens || isUsps
            ? `<img class="neo-filters-flex-item-image" src="${getFilterImageUrl(filter.label, value.label)}" alt="${value.label}" width="100%" height="100%" data-filter-label="${encodedFilterLabel}" data-value-label="${encodedValueLabel}" data-image-attempt="0" onerror="handleFilterImageError(this)">`
            : '';

          let checkBox = '';
          if (isColor) {
            const colorHex = getFilterImageUrl(filter.label, value.label);
            // You probably map color to image — but if you have actual hex, use that instead

            const activeStroke = active ? `stroke="${value.label}"` : '';

            checkBox = `
              <span class="neo-filters-color-checkbox ${active ? 'active' : ''}" data-color="${value.label}">
                ${COLOR_CHECKBOX_SVG(active ? '#ffffff' : '#e1ffe11a')}
              </span>
            `;
          }

          const descriptionKey = `${filter.label.toLowerCase().trim()}:${value.label.toLowerCase().trim()}`;
          const description = state.filterDescriptions[descriptionKey] || '';

          return `
          <div class="neo-filters-item neo-filters-flex-item ${activeClass} ${disabledClass}" data-filter-id="${filter.id}" data-filter-value="${value.label}" ${disabled ? 'data-disabled="true"' : ''} role="button" tabindex="0">
          ${!isColor ? `<span class="neo-filters-clear-filter-icon glass-effect relative">
              <svg width="6" height="6" viewBox="0 0 6 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M0.707031 -3.09053e-08L2.85352 2.14648L5 -2.18557e-07L5.70703 0.707031L3.56055 2.85352L5.70703 5L5 5.70703L2.85352 3.56055L0.707031 5.70703L-3.09035e-08 5L2.14648 2.85352L-2.18557e-07 0.707031L0.707031 -3.09053e-08Z" fill="white"/>
              </svg>
            </span>` : ''}

            ${isColor ? `<div class="flex flex-row gap-5 items-center justify-start">` : ``}
              ${imgDisplay}
              <div style="flex: 1;">
                <p class="neo-filters-flex-item-text">${value.label}</p>
                ${description ? `<p class="neo-filters-flex-item-description line-clamp-1" ${description.length > 25 ? 'data-description-text' : ''}>${description}</p>` : ''}
              </div>
            ${isColor ? `</div>` : ``}


            ${checkBox}
            ${description && description.length > 25 ?
              `<span class="neo-filters-flex-item-icon glass-effect relative cursor-pointer" data-description-toggle="true">
                <svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 19 19" fill="none">
                  <rect width="19" height="19" rx="9.5" fill="black" fill-opacity="0.01"/>
                  <path d="M8.56493 13V7.48889H10.0094V13H8.56493ZM8.59826 6.57778V5.17778H9.99826V6.57778H8.59826Z" fill="#EE3224"/>
                </svg>
              </span>`
              : ''}
          </div>
        `;
        }).join('');

        filterContent = `
        <div class="${flexClass}">
          ${valuesHtml}
        </div>
      `;
      }
    }

    const shouldShowCount = activeCount > 0 && !isPrice;
    const countTagHtml = shouldShowCount
      ? `<p class="neo-filters-accordion-title-text-count">${activeCount}</p>`
      : '';
    const titleBlockHtml = `<div class="neo-title-count-container">
      <p class="neo-filters-accordion-title-text">${filter.label}</p>
      ${countTagHtml}
    </div>`;

    return `
      <div class="neo-filters-accordion ${accordionActiveClass} ${filter.id.replace(/\./g, "-")}" data-accordion-id="${accordionId}">
        <div class="neo-filters-accordion-title" role="button" tabindex="0" aria-expanded="${ariaExpanded}" aria-controls="${accordionId}-content">
          ${titleBlockHtml}
          <span class="neo-filters-accordion-title-icon glass-effect relative">${ACCORDION_ARROW_SVG}</span>
        </div>
        <div class="neo-filters-accordion-content" id="${accordionId}-content">
          <div class="neo-filters-accordion-content-inner">
            ${filterContent}
          </div>
        </div>
      </div>
    `;
  }).join('');

  isRenderingFiltersOnLoad = false;

  container.innerHTML = html;
  initFilterAccordions();
  initFilterValueClicks();
  initCoverageGenderToggle();
  initCoverageActiveTagRemoval();
  hasActiveFilters() ? showClearAllButton() : hideClearAllButton();
  setMaxHeight();
}

function showClearAllButton() {
  const clearAllButton = document.querySelector('.neo-filter-header-clear-all');
  if (clearAllButton) {
    clearAllButton.classList.add('show');
    clearAllButton.classList.remove('hide');
  }
}

function hideClearAllButton() {
  const clearAllButton = document.querySelector('.neo-filter-header-clear-all');
  if (clearAllButton) {
    clearAllButton.classList.remove('show');
    clearAllButton.classList.add('hide');
  }
}

function COLOR_CHECKBOX_SVG(color = '#ffffff') {
  return `
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M9 22H15C20 22 22 20 22 15V9C22 4 20 2 15 2H9C4 2 2 4 2 9V15C2 20 4 22 9 22Z" stroke="${color}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M7.75 12L10.58 14.83L16.25 9.17" stroke="${color}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>
  `;
}

function setCoverageFilter(filter) {
  const currentGender = state.coverageGender || 'Women';
  const flexClass = `neo-filters-flex neo-filters-coverage hide-scrollbar`;

  // Gender toggle HTML
  const genderToggleHtml = `
    <div class="neo-coverage-gender-toggle">
      <button type="button" class="neo-coverage-gender-btn ${currentGender === 'Men' ? 'active' : ''}" data-gender="Men">
        Men
      </button>
      <button type="button" class="neo-coverage-gender-btn ${currentGender === 'Women' ? 'active' : ''}" data-gender="Women">
        Women
      </button>
    </div>
  `;

  // Coverage options HTML
  const valuesHtml = (filter.values || []).map((value) => {
    const active = state.activeFilters[filter.id] && state.activeFilters[filter.id].includes(value.label);
    const disabled = value.count === 0;
    const activeClass = active ? 'active' : '';
    const disabledClass = disabled ? 'disabled' : '';
    const borderColor = COVERAGE_OPTION_COLORS[value.label] || '#FFFFFF';

    const descriptionKey = `${filter.label.toLowerCase().trim()}:${value.label.toLowerCase().trim()}`;
    const description = state.filterDescriptions[descriptionKey] || '';

    return `
      <div class="neo-filters-item neo-filters-coverage-item ${activeClass} ${disabledClass}" data-filter-id="${filter.id}" data-filter-value="${value.label}" ${disabled ? 'data-disabled="true"' : ''} role="button" tabindex="0">
        <div class="neo-coverage-checkbox" style="border-color: ${borderColor};">
          ${active ? `<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 3L4.5 8.5L2 6" stroke="${borderColor}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>` : ''}
        </div>
        <div style="flex: 1;">
          <p class="neo-filters-flex-item-text">${value.label}</p>
          ${description ? `<p class="neo-filters-flex-item-description line-clamp-1">${description}</p>` : ''}
        </div>
      </div>
    `;
  }).join('');

  return `
    ${genderToggleHtml}
    <div class="neo-coverage-layout">
      <div class="neo-coverage-visual">
        <img class="neo-coverage-dynamic-image"
        src="${getCoverageGenderImage()}"
        alt="Coverage levels diagram"
        loading="lazy" />
      </div>
      <div class="neo-coverage-options ${flexClass}">
        ${valuesHtml}
      </div>
    </div>
  `;
}

function getCoverageGenderImage() {
  const gender = state.coverageGender || 'Women';

  const genderSlug = gender.toLowerCase() === 'men' ? 'male' : 'female';

  // Same CDN convention as other filter images
  return `https://cdn.shopify.com/s/files/1/0655/9629/3317/files/filter-image-level-of-coverage-${genderSlug}.png?width=600`;
}

function setFabricFilter(filter) {
  const flexClass = `neo-filters-flex neo-filters-${filter.label.toLowerCase()} hide-scrollbar`;
  const valuesHtml = (filter.values || []).map((value) => {
    const active = state.activeFilters[filter.id] && state.activeFilters[filter.id].includes(value.label);
    const disabled = value.count === 0;
    const activeClass = active ? 'active' : '';
    const disabledClass = disabled ? 'disabled' : '';

    const descriptionKey = `${filter.label.toLowerCase().trim()}:${value.label.toLowerCase().trim()}`;
    const description = state.filterDescriptions[descriptionKey] || '';

    return `
      <div class="neo-filters-item neo-filters-flex-item ${activeClass} ${disabledClass}" data-filter-id="${filter.id}" data-filter-value="${value.label}" ${disabled ? 'data-disabled="true"' : ''} role="button" tabindex="0">
        <span class="neo-filters-clear-filter-icon glass-effect relative">
          <svg width="6" height="6" viewBox="0 0 6 6" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0.707031 -3.09053e-08L2.85352 2.14648L5 -2.18557e-07L5.70703 0.707031L3.56055 2.85352L5.70703 5L5 5.70703L2.85352 3.56055L0.707031 5.70703L-3.09035e-08 5L2.14648 2.85352L-2.18557e-07 0.707031L0.707031 -3.09053e-08Z" fill="white"/>
          </svg>
        </span>
        <div style="flex: 1;">
          <p class="neo-filters-flex-item-text">${value.label}</p>
          ${description ? `
            <p class="neo-filters-flex-item-description line-clamp-1"
              ${description.length > 25 ? 'data-description-text' : ''}>
              ${description}
            </p>
          ` : ''}
        </div>
        ${description && description.length > 25 ?
        `<span class="neo-filters-flex-item-icon glass-effect relative cursor-pointer" data-description-toggle="true">
            <svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 19 19" fill="none">
              <rect width="19" height="19" rx="9.5" fill="black" fill-opacity="0.01"/>
              <path d="M8.56493 13V7.48889H10.0094V13H8.56493ZM8.59826 6.57778V5.17778H9.99826V6.57778H8.59826Z" fill="#EE3224"/>
            </svg>
          </span>`
        : ''}
      </div>
    `;
  }).join('');

  return `
    <div class="${flexClass}">
      ${valuesHtml}
    </div>
  `;
}

function setMaxHeight() {
  // Important: We need to use window.innerWidthCached to get the correct width of the window
  let x = window.innerWidthCached;
  const accordions = document.querySelectorAll('.neo-filters-accordion');

  accordions.forEach((accordion) => {
    const accordionContent = accordion.querySelector('.neo-filters-accordion-content');
    if (!accordionContent) return;
    requestAnimationFrame(() => {
      const maxHeight = Math.min(accordionContent.scrollHeight, 490);
      if (accordionContent) {
        accordion.style.setProperty('--max-height', `${maxHeight}px`);
      }
    })
  });
}

function initFilterAccordions() {
  const wrapper = document.querySelector('.neo-collection-wrapper');
  if (!wrapper) return;
  wrapper.querySelectorAll('.neo-filters-accordion-title').forEach((title) => {
    title.addEventListener('click', () => {
      const accordion = title.closest('.neo-filters-accordion');
      if (!accordion) return;
      setTimeout(() => {
        const wasOpen = accordion.classList.contains('active');
        // Close all accordions (only one open at a time)
        wrapper.querySelectorAll('.neo-filters-accordion').forEach((acc) => {
          acc.classList.remove('active');
          const accordionTitle = acc.querySelector('.neo-filters-accordion-title');
          if (accordionTitle) accordionTitle.setAttribute('aria-expanded', 'false');
        });
        // If the clicked one was closed, open it
        if (!wasOpen) {
          accordion.classList.add('active');
            title.setAttribute('aria-expanded', 'true');
          }
      }, 0);
    });
  });
}

function initFilterValueClicks() {
  const wrapper = document.querySelector('.neo-collection-wrapper');
  if (!wrapper) return;

  wrapper.querySelectorAll('.neo-filters-item[data-filter-id][data-filter-value]').forEach((item) => {
    const handleClick = (e) => {
      // PHASE 1 — Batch DOM reads (synchronous, reads only)
      const isDisabled = item.getAttribute('data-disabled') === 'true';
      const filterId = item.getAttribute('data-filter-id');
      const filterValue = item.getAttribute('data-filter-value');
      const isCoverageItem = item.classList.contains('neo-filters-coverage-item');
      const colorCheckbox = item.querySelector('.neo-filters-color-checkbox');
      const activeValues = state.activeFilters[filterId];
      const idx = activeValues ? activeValues.indexOf(filterValue) : -1;
      const isAdding = idx === -1;
      const borderColor =
        isAdding && isCoverageItem && colorCheckbox
          ? colorCheckbox.style.borderColor || '#FFFFFF'
          : null;

      if (isDisabled) return;
      e.preventDefault();
      if (!filterId || !filterValue) return;

      // PHASE 2 — Critical visual updates (synchronous, writes only)
      if (isAdding) {
        item.classList.add('active');
        if (colorCheckbox) {
          colorCheckbox.innerHTML = COLOR_CHECKBOX_SVG(filterValue);
        }
        if (colorCheckbox && isCoverageItem) {
          colorCheckbox.innerHTML = `<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 3L4.5 8.5L2 6" stroke="${borderColor}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>`;
        }
      } else {
        item.classList.remove('active');
        if (colorCheckbox) {
          colorCheckbox.innerHTML = COLOR_CHECKBOX_SVG('#ffffff');
        }
        if (colorCheckbox && isCoverageItem) {
          colorCheckbox.innerHTML = '';
        }
      }

      // PHASE 3 — Deferred expensive work
      setTimeout(() => {
        if (!state.activeFilters[filterId]) state.activeFilters[filterId] = [];
        if (isAdding) {
          state.activeFilters[filterId].push(filterValue);
        } else {
          state.activeFilters[filterId].splice(idx, 1);
        }
        if (state.activeFilters[filterId].length === 0) delete state.activeFilters[filterId];
        applyFilters();
      }, 0);

    };

    // Main click handler on the whole pill
    item.addEventListener('click', handleClick);

    // Ensure clicking directly on the coverage checkbox also toggles
    const coverageCheckbox = item.querySelector('.neo-coverage-checkbox');
    if (coverageCheckbox) {
      coverageCheckbox.addEventListener('click', (e) => {
        e.stopPropagation();
        handleClick(e);
      });
    }
  });

  // Prevent filter toggle when clicking fabric info icon
  wrapper.querySelectorAll('[data-description-toggle="true"]').forEach(icon => {
    icon.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();

      const parent = icon.closest('.neo-filters-item');
      const desc = parent?.querySelector('[data-description-text]');
      if (!desc) return;

      desc.classList.toggle('line-clamp-1');
    });
  });

  if (typeof ReloadSmartWishlist == 'function') ReloadSmartWishlist()

}

function initCoverageGenderToggle() {
  const wrapper = document.querySelector('.neo-collection-wrapper');
  if (!wrapper) return;
  wrapper.querySelectorAll('.neo-coverage-gender-btn').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const gender = btn.getAttribute('data-gender');
      if (!gender) return;

      // Update state for coverage UI
      state.coverageGender = gender;

      // Update coverage image instantly (all instances)
      document.querySelectorAll('.neo-coverage-dynamic-image').forEach((img) => {
        img.src = getCoverageGenderImage();
      });

      // Also treat this as a gender filter selection
      const genderFilterId = 'filter.p.m.custom.gender_filter';

      // Initialize gender filter array if needed
      if (!state.activeFilters[genderFilterId]) {
        state.activeFilters[genderFilterId] = [];
      }

      // Always keep a single selected gender from this toggle
      state.activeFilters[genderFilterId] = [gender];

      // Update UI
      wrapper.querySelectorAll('.neo-coverage-gender-btn').forEach((b) => {
        b.classList.remove('active');
      });
      btn.classList.add('active');

      // Sync the main Gender filter pills, if present
      wrapper
        .querySelectorAll(`.neo-filters-item[data-filter-id="${genderFilterId}"]`)
        .forEach((item) => {
          const value = item.getAttribute('data-filter-value');
          if (value === gender) {
            item.classList.add('active');
          } else {
            item.classList.remove('active');
          }
        });

      // Apply filters so results update based on gender
      applyFilters();

    });
  });
}

function initCoverageActiveTagRemoval() {
  const wrapper = document.querySelector('.neo-collection-wrapper');
  if (!wrapper) return;
  wrapper.querySelectorAll('.neo-coverage-active-tag-remove').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const filterId = btn.getAttribute('data-filter-id');
      const filterValue = btn.getAttribute('data-filter-value');
      if (!filterId || !filterValue) return;

      // Remove the filter
      if (state.activeFilters[filterId]) {
        const idx = state.activeFilters[filterId].indexOf(filterValue);
        if (idx !== -1) {
          state.activeFilters[filterId].splice(idx, 1);
          if (state.activeFilters[filterId].length === 0) {
            delete state.activeFilters[filterId];
          }
        }
      }

      // Re-render filters to update the UI
      renderFilters(state.filters);
      if (state.priceRange.currentMin != null && state.priceRange.currentMax != null) {
        globalPriceRangeState.setState({
          minValue:
            ((state.priceRange.currentMin - state.priceRange.min) /
              (state.priceRange.max - state.priceRange.min)) *
            100,
          maxValue:
            ((state.priceRange.currentMax - state.priceRange.min) /
              (state.priceRange.max - state.priceRange.min)) *
            100,
        });
      }
      applyFilters();
    });
  });
}

/*
  ┌─────────────────────────────────────────────────────────────────────────┐
  │ Clear all filters                                                       │
  └─────────────────────────────────────────────────────────────────────────┘
*/

function clearAllFilters() {
  state.activeFilters = {};

  state.priceRange.currentMin = state.priceRange.min;
  state.priceRange.currentMax = state.priceRange.max;

  // BUG 7 FIX: reset global price range state so the next neo-price-range
  // mount (from renderFilters) picks up the full range correctly.
  globalPriceRangeState.setState({
    minValue: 0,
    maxValue: 100,
  });

  const priceEl = document.querySelector('neo-price-range');
  if (priceEl && state.priceRange.min != null && state.priceRange.max != null) {
    // BUG 7 FIX: use the correct observed attribute names ('min-value'/'max-value'),
    // not the unobserved 'initial-min'/'initial-max' that were previously used.
    priceEl.setAttribute('min-value', state.priceRange.min);
    priceEl.setAttribute('max-value', state.priceRange.max);
  }

  document.querySelectorAll('.neo-filters-grid-item.active, .neo-filters-flex-item.active').forEach(el => {
    el.classList.remove('active');
  });

  state.currentSort.sortValue = null;
  state.currentSort.sortKey = null;
  state.currentSort.reverse = false;

  const firstOption = document.querySelector('[data-sort-select] .custom-option');
  const selectedText = document.querySelector('[data-sort-select] .selected-value');

  if (firstOption && selectedText) {
    selectedText.textContent = firstOption.textContent;
    document.querySelectorAll('[data-sort-select] .custom-option').forEach(opt => opt.classList.remove('is-selected'));
    firstOption.classList.add('is-selected');
  }

  delete state.queryParams.sort_by;

  updateURLQueryParams();
  setProductCount(true);
  applyFilters();
}

globalThis.clearAllFilters = clearAllFilters;

/*
  ┌─────────────────────────────────────────────────────────────────────────┐
  │ Skeleton loaders                                                        │
  └─────────────────────────────────────────────────────────────────────────┘
*/

const SKELETON_STYLE = `
  --skeleton-width: 100%;
  --skeleton-height: 100%;
  --skeleton-min-height: 559px;
  --skeleton-radius: 13px;
  --skeleton-mt: 6px;
  --skeleton-bg: rgba(170, 170, 170,0.2);
  --skeleton-shine-color: rgba(255, 255, 255, 0.3);
  --skeleton-animation-duration: 1.7s;
`;

function generateSkeletonLoaders(count, replace = false) {
  const productGrid = document.getElementById('product-grid');
  if (!productGrid) return;

  const skeletonHTML = `<li class="grid__item grid__item--skeleton"><skeleton style="${SKELETON_STYLE}"></skeleton></li>`;

  if (replace) {
    // Every li.grid__item is being turned into a skeleton anyway, so replace them
    // all in one innerHTML write instead of one outerHTML mutation per item —
    // each of those was its own forced style/layout recalc.
    const existingCount = productGrid.querySelectorAll('li.grid__item').length;
    const total = Math.max(existingCount, count);
    productGrid.innerHTML = skeletonHTML.repeat(total);
  } else {
    // Must stay synchronous for the same reason as the replace path above.
    productGrid.insertAdjacentHTML('beforeend', skeletonHTML.repeat(count));
  }
}

/*
  ┌─────────────────────────────────────────────────────────────────────────┐
  │ Product card HTML (matches neo-product-card-1.liquid structure)         │
  └─────────────────────────────────────────────────────────────────────────┘
*/

function formatPrice(amount) {
  const price = Number.parseFloat(amount);
  return `₹ ${new Intl.NumberFormat('en-IN').format(Math.floor(price))}`;
}

function formatAtcPriceHtml(priceAmount, compareAtAmount) {
  if (window.AtcPriceBlock && typeof window.AtcPriceBlock.html === 'function') {
    return window.AtcPriceBlock.html(priceAmount, compareAtAmount);
  }
  return `<span class="atc-price-block"><span class="atc-price-block__sale">${formatPrice(priceAmount)}</span></span>`;
}

function getProductAtcPrices(product) {
  const firstVariant = product?.variants?.edges?.[0]?.node;
  const minPrice = Number.parseFloat(
    firstVariant?.price?.amount ||
    product?.priceRange?.minVariantPrice?.amount ||
    0
  );
  const compareAtPrice = Number.parseFloat(
    firstVariant?.compareAtPrice?.amount ||
    product?.compareAtPriceRange?.minVariantPrice?.amount ||
    0
  );
  return { minPrice, compareAtPrice };
}

function getProductImage(product) {
  const featured = product.featuredImage;
  if (featured?.url) {
    const src = featured.url;
    const separator = src.includes('?') ? '&' : '?';
    return {
      src: `${src}${separator}width=700`,
      alt: featured.altText || product.title,
      width: featured.width || 700,
      height: featured.height || 1000
    };
  }
  const images = product.images?.nodes || [];
  const imageData = images.length > 0 ? images[0] : null;
  if (!imageData) return null;
  const src = imageData.url || imageData.src || '';
  const separator = src.includes('?') ? '&' : '?';
  return {
    src: `${src}${separator}width=700`,
    alt: imageData.altText || product.title,
    width: imageData.width || 700,
    height: imageData.height || 1000
  };
}

function getWatchGifAndVideo(product) {
  const mediaEdges = product.media?.edges || [];
  let watchGifSrc = '';
  let videoPreviewUrl = '';
  let videoSourceUrl = '';
  const watchGifImage = product.metafield_watch_gif?.reference?.image?.url;
  if (watchGifImage) watchGifSrc = watchGifImage;

  for (let i = 0; i < mediaEdges.length; i++) {
    const node = mediaEdges[i]?.node;
    if (i <= 2 || !node) continue;
    const isVideo = node.mediaContentType === 'VIDEO' || node.__typename === 'Video' || (node.sources && !node.image);
    if (isVideo) {
      if (!watchGifSrc && node.previewImage?.url) watchGifSrc = node.previewImage.url;
      if (node.previewImage?.url) videoPreviewUrl = node.previewImage.url;
      const mp4 = node.sources?.find?.(s => s.mimeType === 'video/mp4');
      if (mp4?.url) videoSourceUrl = mp4.url;
      break;
    }
  }
  return { watchGifSrc, videoPreviewUrl, videoSourceUrl };
}

function getProductMediaJson(product) {
  const mediaEdges = product.media?.edges || [];
  const arr = mediaEdges.map((e) => {
    const n = e?.node;
    if (!n) return null;
    const isImage = n.mediaContentType === 'IMAGE' || n.__typename === 'MediaImage' || n.image;
    const isVideo = n.mediaContentType === 'VIDEO' || n.__typename === 'Video' || n.sources;
    if (isImage && n.image) {
      return { media_type: 'image', preview_image: { url: n.image.url }, sources: [] };
    }
    if (isVideo) {
      return {
        media_type: 'video',
        preview_image: n.previewImage ? { url: n.previewImage.url } : null,
        sources: (n.sources || []).map((s) => ({ mime_type: s.mimeType, url: s.url }))
      };
    }
    return null;
  }).filter(Boolean);
  return JSON.stringify(arr).replace(/'/g, '&#39;');
}

function getColorVariantSwatches(product) {
  const refs = product.metafield_color_variants?.references?.nodes || [];
  const swatches = [];
  for (const node of refs) {
    if (swatches.length >= 4) break;
    if (!node?.availableForSale) continue;
    const imgRef = node.metafield_color_swatch_image?.reference?.image?.url;
    if (imgRef) swatches.push({ url: imgRef, alt: product.title });
  }
  return swatches;
}

function getHighlightsString(product) {
  const raw = product.metafield_highlights?.value;
  if (!raw) return '';
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return Array.isArray(parsed) ? parsed.join(' | ') : String(parsed);
  } catch {
    return String(raw);
  }
}

function generateProductCardHTML(product, index) {
  const productId = product.id.replace('gid://shopify/Product/', '');
  const variantId = product.variants?.edges?.[0]?.node?.id?.replace('gid://shopify/ProductVariant/', '') || '';
  const productUrl = `/products/${product.handle}`;
  const productTitle = product.title;
  const primaryImage = getProductImage(product);
  const imageSrc = primaryImage?.src || '';
  const imageAlt = primaryImage?.alt || productTitle;
  const { minPrice, compareAtPrice } = getProductAtcPrices(product);
  const formattedAtcPrice = formatAtcPriceHtml(minPrice, compareAtPrice);

  const { watchGifSrc, videoPreviewUrl, videoSourceUrl } = getWatchGifAndVideo(product);
  const hasProductVideo = !!(product.media?.edges?.some((e, i) => {
    const n = e?.node;
    return i > 2 && n && (n.mediaContentType === 'VIDEO' || n.__typename === 'Video' || (n.sources && !n.image));
  }));
  const showWatchGif = hasProductVideo && watchGifSrc;

  const productMediaJson = getProductMediaJson(product);
  const colorSwatches = getColorVariantSwatches(product);
  const highlightsStr = getHighlightsString(product);
  const tag = product.metafield_tag?.value ?? '';

  const colorVariantIds = (product.metafield_color_variants?.references?.nodes || [])
    .map((n) => (n?.id || '').replace('gid://shopify/Product/', ''))
    .filter(Boolean)
    .join(',');

  const loadingAttr = index < 4 ? 'eager' : 'lazy';

  const colorSwatchesHtml = colorSwatches.map((sw) => {
    const swUrl = sw.url + (sw.url.includes('?') ? '&' : '?') + 'width=100';
    return `<img src="${swUrl}" alt="${(sw.alt || productTitle).replace(/"/g, '&quot;')}" class="neo-product-card-color-variant-image" loading="lazy">`;
  }).join('');

  const watchGifBlock = showWatchGif
    ? `<div class="neo-product-card-1-watch-gif-container">
        <div class="relative">
          <img src="${watchGifSrc + (watchGifSrc.includes('?') ? '&' : '?') + 'width=100'}" alt="${(productTitle).replace(/"/g, '&quot;')}" class="neo-product-card-1-watch-gif" loading="lazy">
          ${NEO_PLAY_ICON_SVG}
        </div>
        <p class="neo-product-card-1-watch-gif-text">Watch</p>
      </div>`
    : '';

  const tagBlock = tag ? `<p class="neo-product-card-1-tag">${String(tag).replace(/</g, '&lt;').replace(/>/g, '&gt;')}</p>` : '';
  const showNewTag = (product.tags || []).join(' ').toLowerCase().includes('new 2026');
  const showSaleTag = (product.tags || []).join(' ').toLowerCase().includes('sale 2026');
  const newTagBlock = showNewTag ? '<p class="neo-product-card-new-tag">New</p>' : '';
  const saleTagBlock = showSaleTag ? '<p class="neo-product-card-sale-tag">Sale</p>' : '';
  const buttonConor2 = `<svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none">
    <mask id="" fill="white">
    <path d="M0 2C0 0.895431 0.895431 0 2 0H6C7.10457 0 8 0.895431 8 2V6C8 7.10457 7.10457 8 6 8H2C0.895431 8 0 7.10457 0 6V2Z"></path>
    </mask>
    <path d="M-0.5 2C-0.5 0.619288 0.619288 -0.5 2 -0.5H5.5C6.88071 -0.5 8 0.619288 8 2C8 1.17157 7.10457 0.5 6 0.5H2C1.17157 0.5 0.5 1.17157 0.5 2H-0.5ZM8 8H0H8ZM2 8C0.619288 8 -0.5 6.88071 -0.5 5.5V2C-0.5 0.619288 0.619288 -0.5 2 -0.5V0.5C1.17157 0.5 0.5 1.17157 0.5 2V6C0.5 7.10457 1.17157 8 2 8ZM8 0V8V0Z" fill="#ffffff" mask="url(#)"></path>
  </svg>`
  const buttonConor1 = `<svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none">
    <mask id="" fill="white">
    <path d="M0 2C0 0.895431 0.895431 0 2 0H6C7.10457 0 8 0.895431 8 2V6C8 7.10457 7.10457 8 6 8H2C0.895431 8 0 7.10457 0 6V2Z"></path>
    </mask>
    <path d="M-0.5 2C-0.5 0.619288 0.619288 -0.5 2 -0.5H5.5C6.88071 -0.5 8 0.619288 8 2C8 1.17157 7.10457 0.5 6 0.5H2C1.17157 0.5 0.5 1.17157 0.5 2H-0.5ZM8 8H0H8ZM2 8C0.619288 8 -0.5 6.88071 -0.5 5.5V2C-0.5 0.619288 0.619288 -0.5 2 -0.5V0.5C1.17157 0.5 0.5 1.17157 0.5 2V6C0.5 7.10457 1.17157 8 2 8ZM8 0V8V0Z" fill="#ffffff" mask="url(#)"></path>
  </svg>`
  const buttonConor3 = `<svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none">
    <mask id="" fill="white">
    <path d="M0 2C0 0.895431 0.895431 0 2 0H6C7.10457 0 8 0.895431 8 2V6C8 7.10457 7.10457 8 6 8H2C0.895431 8 0 7.10457 0 6V2Z"></path>
    </mask>
    <path d="M-0.5 2C-0.5 0.619288 0.619288 -0.5 2 -0.5H5.5C6.88071 -0.5 8 0.619288 8 2C8 1.17157 7.10457 0.5 6 0.5H2C1.17157 0.5 0.5 1.17157 0.5 2H-0.5ZM8 8H0H8ZM2 8C0.619288 8 -0.5 6.88071 -0.5 5.5V2C-0.5 0.619288 0.619288 -0.5 2 -0.5V0.5C1.17157 0.5 0.5 1.17157 0.5 2V6C0.5 7.10457 1.17157 8 2 8ZM8 0V8V0Z" fill="#ffffff" mask="url(#)"></path>
  </svg>`
  const buttonConor4 = `<svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none">
    <mask id="" fill="white">
    <path d="M0 2C0 0.895431 0.895431 0 2 0H6C7.10457 0 8 0.895431 8 2V6C8 7.10457 7.10457 8 6 8H2C0.895431 8 0 7.10457 0 6V2Z"></path>
    </mask>
    <path d="M-0.5 2C-0.5 0.619288 0.619288 -0.5 2 -0.5H5.5C6.88071 -0.5 8 0.619288 8 2C8 1.17157 7.10457 0.5 6 0.5H2C1.17157 0.5 0.5 1.17157 0.5 2H-0.5ZM8 8H0H8ZM2 8C0.619288 8 -0.5 6.88071 -0.5 5.5V2C-0.5 0.619288 0.619288 -0.5 2 -0.5V0.5C1.17157 0.5 0.5 1.17157 0.5 2V6C0.5 7.10457 1.17157 8 2 8ZM8 0V8V0Z" fill="#ffffff" mask="url(#)"></path>
  </svg>`


  return `
    <li class="grid__item" data-product-id="${productId}">
      <neo-product-card-1 class="neo-product-card-1"
        data-product-id="${productId}"
        data-product-watch-gif-src="${(watchGifSrc || '').replace(/"/g, '&quot;')}"
        data-product-video-preview-image-url="${(videoPreviewUrl || '').replace(/"/g, '&quot;')}"
        data-product-video-source-url="${(videoSourceUrl || '').replace(/"/g, '&quot;')}"
        data-enable-swiper="true"
        data-enable-pagination="true">
        <div class="neo-product-card-details">
          <div class="title-container">
            <a class="title-link-wrapper" href="${productUrl}">
              <h2 class="visually-hidden">${(productTitle).replace(/</g, '&lt;').replace(/>/g, '&gt;')}</h2>
              <p class="neo-product-card-title line-clamp-2 min-height">${(productTitle).replace(/</g, '&lt;').replace(/>/g, '&gt;')}</p>
              <p class="neo-product-card-sub-title line-clamp-2 min-height">${(highlightsStr || '').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</p>
            </a>
          </div>
          <div class="neo-product-card-wishlist">
            <span class="smartwishlist" data-product="${productId}" data-variant="${variantId}"></span>
          </div>
        </div>
        ${watchGifBlock}
         <div class="color-variant-images">
              ${colorSwatchesHtml}
         </div>
        <div class="neo-product-card-media-container">
          ${newTagBlock}
          ${saleTagBlock}
          <div class="neo-product-card-media">
            <div class="swiper">
              <div class="swiper-wrapper">
                <div class="swiper-slide">
                  <a class="neo-product-card-media--link" href="${productUrl}">
                    <img
                      src="${imageSrc}&width=520"
                      alt="${(imageAlt).replace(/"/g, '&quot;')}"
                      class="neo-product-card-1__image-img"
                      width="700"
                      height="1000"
                      loading="${loadingAttr}"
                    >
                  </a>
                </div>
              </div>
            </div>

            ${tagBlock}
          </div>
          <button type="button" class="add-to-cart-button-gql" onclick="ATBModalOpen(event, '${productId}')" data-product-id="${productId}" data-color-variants-ids="${(colorVariantIds || '').replace(/"/g, '&quot;')}">
            ${formattedAtcPrice}
            <span>|</span>
            ${window.AtcPriceBlock && window.AtcPriceBlock.ctaPlusHtml ? window.AtcPriceBlock.ctaPlusHtml() : '<span class="add-to-cart-button-gql-plus"><span class="add-to-cart-button-gql-plus-icon">+</span><span class="add-to-cart-button-gql-label">Add</span></span>'}
            <div class="corner-1">${buttonConor1}</div>
            <div class="corner-2">${buttonConor2}</div>
            <div class="corner-3">${buttonConor3}</div>
            <div class="corner-4">${buttonConor4}</div>
          </button>
        </div>
      </neo-product-card-1>
    </li>`;
}

function generateCardForInGridFeaturedProduct(product, index) {
  const productId = product.id.replace('gid://shopify/Product/', '');
  const variantId = product.variants?.edges?.[0]?.node?.id?.replace('gid://shopify/ProductVariant/', '') || '';
  const productUrl = `/products/${product.handle}`;
  const productTitle = product.title;
  const primaryImage = getProductImage(product);
  const imageSrc = primaryImage?.src || '';
  const imageAlt = primaryImage?.alt || productTitle;
  const { minPrice, compareAtPrice } = getProductAtcPrices(product);
  const formattedAtcPrice = formatAtcPriceHtml(minPrice, compareAtPrice);

  const { watchGifSrc, videoPreviewUrl, videoSourceUrl } = getWatchGifAndVideo(product);
  const hasProductVideo = !!(product.media?.edges?.some((e, i) => {
    const n = e?.node;
    return i > 2 && n && (n.mediaContentType === 'VIDEO' || n.__typename === 'Video' || (n.sources && !n.image));
  }));
  const showWatchGif = hasProductVideo && watchGifSrc;

  const productMediaJson = getProductMediaJson(product);
  const colorSwatches = getColorVariantSwatches(product);
  const highlightsStr = getHighlightsString(product);
  const tag = product.metafield_tag?.value ?? '';

  const colorVariantIds = (product.metafield_color_variants?.references?.nodes || [])
    .map((n) => (n?.id || '').replace('gid://shopify/Product/', ''))
    .filter(Boolean)
    .join(',');

  const loadingAttr = index < 4 ? 'eager' : 'lazy';

  const colorSwatchesHtml = colorSwatches.map((sw) => {
    const swUrl = sw.url + (sw.url.includes('?') ? '&' : '?') + 'width=100';
    return `<img src="${swUrl}" alt="${(sw.alt || productTitle).replace(/"/g, '&quot;')}" class="neo-product-card-color-variant-image" loading="lazy">`;
  }).join('');

  const watchGifBlock = showWatchGif
    ? `<div class="neo-product-card-1-watch-gif-container">
        <div class="relative">
          <img src="${watchGifSrc + (watchGifSrc.includes('?') ? '&' : '?') + 'width=100'}" alt="${(productTitle).replace(/"/g, '&quot;')}" class="neo-product-card-1-watch-gif" loading="lazy">
          ${NEO_PLAY_ICON_SVG}
        </div>
        <p class="neo-product-card-1-watch-gif-text">Watch</p>
      </div>`
    : '';

  const tagBlock = tag ? `<p class="neo-product-card-1-tag">${String(tag).replace(/</g, '&lt;').replace(/>/g, '&gt;')}</p>` : '';
  const buttonConor2 = `<svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none">
  <mask id="" fill="white">
  <path d="M0 2C0 0.895431 0.895431 0 2 0H6C7.10457 0 8 0.895431 8 2V6C8 7.10457 7.10457 8 6 8H2C0.895431 8 0 7.10457 0 6V2Z"></path>
  </mask>
  <path d="M-0.5 2C-0.5 0.619288 0.619288 -0.5 2 -0.5H5.5C6.88071 -0.5 8 0.619288 8 2C8 1.17157 7.10457 0.5 6 0.5H2C1.17157 0.5 0.5 1.17157 0.5 2H-0.5ZM8 8H0H8ZM2 8C0.619288 8 -0.5 6.88071 -0.5 5.5V2C-0.5 0.619288 0.619288 -0.5 2 -0.5V0.5C1.17157 0.5 0.5 1.17157 0.5 2V6C0.5 7.10457 1.17157 8 2 8ZM8 0V8V0Z" fill="#ffffff" mask="url(#)"></path>
</svg>`
  const buttonConor1 = `<svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none">
  <mask id="" fill="white">
  <path d="M0 2C0 0.895431 0.895431 0 2 0H6C7.10457 0 8 0.895431 8 2V6C8 7.10457 7.10457 8 6 8H2C0.895431 8 0 7.10457 0 6V2Z"></path>
  </mask>
  <path d="M-0.5 2C-0.5 0.619288 0.619288 -0.5 2 -0.5H5.5C6.88071 -0.5 8 0.619288 8 2C8 1.17157 7.10457 0.5 6 0.5H2C1.17157 0.5 0.5 1.17157 0.5 2H-0.5ZM8 8H0H8ZM2 8C0.619288 8 -0.5 6.88071 -0.5 5.5V2C-0.5 0.619288 0.619288 -0.5 2 -0.5V0.5C1.17157 0.5 0.5 1.17157 0.5 2V6C0.5 7.10457 1.17157 8 2 8ZM8 0V8V0Z" fill="#ffffff" mask="url(#)"></path>
</svg>`
  const buttonConor3 = `<svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none">
  <mask id="" fill="white">
  <path d="M0 2C0 0.895431 0.895431 0 2 0H6C7.10457 0 8 0.895431 8 2V6C8 7.10457 7.10457 8 6 8H2C0.895431 8 0 7.10457 0 6V2Z"></path>
  </mask>
  <path d="M-0.5 2C-0.5 0.619288 0.619288 -0.5 2 -0.5H5.5C6.88071 -0.5 8 0.619288 8 2C8 1.17157 7.10457 0.5 6 0.5H2C1.17157 0.5 0.5 1.17157 0.5 2H-0.5ZM8 8H0H8ZM2 8C0.619288 8 -0.5 6.88071 -0.5 5.5V2C-0.5 0.619288 0.619288 -0.5 2 -0.5V0.5C1.17157 0.5 0.5 1.17157 0.5 2V6C0.5 7.10457 1.17157 8 2 8ZM8 0V8V0Z" fill="#ffffff" mask="url(#)"></path>
</svg>`
  const buttonConor4 = `<svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none">
  <mask id="" fill="white">
  <path d="M0 2C0 0.895431 0.895431 0 2 0H6C7.10457 0 8 0.895431 8 2V6C8 7.10457 7.10457 8 6 8H2C0.895431 8 0 7.10457 0 6V2Z"></path>
  </mask>
  <path d="M-0.5 2C-0.5 0.619288 0.619288 -0.5 2 -0.5H5.5C6.88071 -0.5 8 0.619288 8 2C8 1.17157 7.10457 0.5 6 0.5H2C1.17157 0.5 0.5 1.17157 0.5 2H-0.5ZM8 8H0H8ZM2 8C0.619288 8 -0.5 6.88071 -0.5 5.5V2C-0.5 0.619288 0.619288 -0.5 2 -0.5V0.5C1.17157 0.5 0.5 1.17157 0.5 2V6C0.5 7.10457 1.17157 8 2 8ZM8 0V8V0Z" fill="#ffffff" mask="url(#)"></path>
</svg>`

  return `
    <div class="in-grid-featured-product-card" data-in-grid-featured-product-id="${productId}">
      <neo-product-card-1 class="neo-product-card-1"
        data-product-id="${productId}"
        data-product-watch-gif-src="${(watchGifSrc || '').replace(/"/g, '&quot;')}"
        data-product-video-preview-image-url="${(videoPreviewUrl || '').replace(/"/g, '&quot;')}"
        data-product-video-source-url="${(videoSourceUrl || '').replace(/"/g, '&quot;')}">
        <div class="neo-product-card-details">
          <div class="title-container">
            <a class="title-link-wrapper" href="${productUrl}">
              <h2 class="visually-hidden">${(productTitle).replace(/</g, '&lt;').replace(/>/g, '&gt;')}</h2>
              <p class="neo-product-card-title line-clamp-2 min-height">${(productTitle).replace(/</g, '&lt;').replace(/>/g, '&gt;')}</p>
              <p class="neo-product-card-sub-title line-clamp-2 min-height">${(highlightsStr || '').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</p>
            </a>
          </div>
          <div class="neo-product-card-wishlist">
            <span class="smartwishlist" data-product="${productId}" data-variant="${variantId}"></span>
          </div>
        </div>
        ${watchGifBlock}
           <div class="color-variant-images">
              ${colorSwatchesHtml}
            </div>
        <div class="neo-product-card-media-container">
          <div class="neo-product-card-media">
            <div class="swiper">
              <div class="swiper-wrapper">
                <div class="swiper-slide">
                  <a class="neo-product-card-media--link" href="${productUrl}">
                    <img
                      src="${imageSrc}&width=520"
                      alt="${(imageAlt).replace(/"/g, '&quot;')}"
                      class="neo-product-card-1__image-img"
                      width="700"
                      height="1000"
                      loading="${loadingAttr}"
                    >
                  </a>
                </div>
              </div>
            </div>
            ${tagBlock}
          </div>
          <button type="button" class="add-to-cart-button-gql" onclick="ATBModalOpen(event, '${productId}')" data-product-id="${productId}" data-color-variants-ids="${(colorVariantIds || '').replace(/"/g, '&quot;')}">
            ${formattedAtcPrice}
            <span>|</span>
            ${window.AtcPriceBlock && window.AtcPriceBlock.ctaPlusHtml ? window.AtcPriceBlock.ctaPlusHtml() : '<span class="add-to-cart-button-gql-plus"><span class="add-to-cart-button-gql-plus-icon">+</span><span class="add-to-cart-button-gql-label">Add</span></span>'}
            <div class="corner-1">${buttonConor1}</div>
            <div class="corner-2">${buttonConor2}</div>
            <div class="corner-3">${buttonConor3}</div>
            <div class="corner-4">${buttonConor4}</div>
          </button>
        </div>
      </neo-product-card-1>
    </div>`;
}

/*
  ┌─────────────────────────────────────────────────────────────────────────┐
  │ Remove liquid-rendered product IDs from list                            │
  └─────────────────────────────────────────────────────────────────────────┘
*/

function extractProductId(productId) {
  if (!productId) return '';
  if (/^\d+$/.test(String(productId).trim())) return String(productId).trim();
  const match = String(productId).match(/gid:\/\/shopify\/Product\/(\d+)/);
  return match ? match[1] : '';
}

function removeProductsByIds(products, idsToRemove) {
  if (!Array.isArray(products) || products.length === 0) return [];
  if (!idsToRemove || typeof idsToRemove !== 'string' || idsToRemove.trim() === '') return products;
  const idsSet = new Set(idsToRemove.split(',').map(id => String(id).trim()).filter(Boolean));
  if (idsSet.size === 0) return products;
  return products.filter((product) => {
    const id = extractProductId(product.id);
    return !idsSet.has(id);
  });
}

/*
  ┌─────────────────────────────────────────────────────────────────────────┐
  │ Product grid rendering                                                  │
  └─────────────────────────────────────────────────────────────────────────┘
*/
function renderCollectionGrid(products, replace = false) {
  const productGrid = document.getElementById('product-grid');
  if (!productGrid) return;

  let toRender = products;
  if (!replace && state.firstLoad && config.productIdRenderedInLiquid) {
    toRender = removeProductsByIds(products, config.productIdRenderedInLiquid);
  }

  if (replace) {
    productGrid.querySelectorAll('li.grid__item').forEach((li) => li.remove());
    state.totalRenderedProducts = 0;
  }

  toRender.forEach((product, i) => {
    const globalIndex = state.totalRenderedProducts + i;
    const cardHTML = generateProductCardHTML(product, globalIndex);
    const temp = document.createElement('div');
    temp.innerHTML = cardHTML.trim();
    const li = temp.firstElementChild;
    if (li) productGrid.appendChild(li);
  });

  state.totalRenderedProducts += toRender.length;
}

/*
  ┌─────────────────────────────────────────────────────────────────────────┐
  │ No products / Show products                                             │
  └─────────────────────────────────────────────────────────────────────────┘
*/

function renderNoProducts() {
  const productGrid = document.getElementById('product-grid');
  const wrapper = document.querySelector('.neo-collection-main-content');
  if (!wrapper) return;
  if (productGrid) productGrid.style.display = 'none';
  let noProducts = wrapper.querySelector('.no-products');
  if (!noProducts) {
    noProducts = document.createElement('div');
    noProducts.className = 'flex justify-center items-center h-full w-full no-products';
    noProducts.innerHTML = `<h2 class="title title--primary center fs-24 fw-600">No products found</h2><p class="center">Use fewer filters or <button type="button" class="link" onclick="clearAllFilters()">Clear All</button></p>`;
    wrapper.appendChild(noProducts);
  }
}

function showProducts() {
  const productGrid = document.getElementById('product-grid');
  const wrapper = document.querySelector('.neo-collection-main-content');
  if (!wrapper) return;
  if (productGrid) productGrid.style.display = '';
  const noProducts = wrapper.querySelector('.no-products');
  if (noProducts) noProducts.remove();
}

/*
  ┌─────────────────────────────────────────────────────────────────────────┐
  │ Apply filters                                                           │
  └─────────────────────────────────────────────────────────────────────────┘
*/

function applyFilters() {
  generateSkeletonLoaders(config.productsPerPage || 12, true);

  state.pagination.cursor = null;
  state.pagination.totalLoaded = 0;
  state.allProducts = [];
  state.totalRenderedProducts = 0;

  // generateSkeletonLoaders(..., true) above already wipes every li.grid__item in
  // #product-grid via a single innerHTML write, including in-grid-section items —
  // so there's nothing left here to query and remove; just reset the tracking Set.
  state.insertedGridSections.clear();

  const graphqlFilters = buildGraphQLFiltersFromActiveFilters();
  fetchAndRenderUI('applyFilters', graphqlFilters, state.currentSort.sortKey, state.currentSort.reverse);
}

// Apply price range changes coming from the neo-price-range web component
document.addEventListener('neo:price-range-change', (event) => {
  const detail = event && event.detail;
  if (!detail || !state.priceRange) return;

  const min = Number(detail.minPrice);
  const max = Number(detail.maxPrice);
  if (!Number.isFinite(min) || !Number.isFinite(max) || max < min) return;

  state.priceRange.currentMin = min;
  state.priceRange.currentMax = max;

  applyFilters();
});

/*
  ┌─────────────────────────────────────────────────────────────────────────┐
  │ Infinite scroll (Intersection Observer)                                 │
  └─────────────────────────────────────────────────────────────────────────┘
*/

let infiniteScrollObserver = null;

function setupIntersectionObserver() {
  if (infiniteScrollObserver) {
    infiniteScrollObserver.disconnect();
    infiniteScrollObserver = null;
  }

  const firstSkeleton = document.querySelector('#product-grid .grid__item--skeleton');
  if (!firstSkeleton || !state.pagination.hasNextPage) return;

  infiniteScrollObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !state.pagination.isLoading && state.pagination.hasNextPage) {
          loadMoreProducts();
        }
      });
    },
    { rootMargin: '400px' }
  );
  infiniteScrollObserver.observe(firstSkeleton);
}

async function loadMoreProducts() {

  if (state.pagination.isLoading || !state.pagination.hasNextPage) return;
  state.pagination.isLoading = true;

  if (infiniteScrollObserver) {
    infiniteScrollObserver.disconnect();
    infiniteScrollObserver = null;
  }

  try {
    const graphqlFilters = buildGraphQLFiltersFromActiveFilters();
    const result = await fetchCollectionData(
      state.pagination.cursor,
      graphqlFilters,
      state.currentSort.sortKey,
      state.currentSort.reverse
    );

    if (result.products.length === 0) {
      state.pagination.hasNextPage = false;
      state.pagination.isLoading = false;
      document.querySelectorAll('#product-grid .grid__item--skeleton').forEach((s) => s.remove());
      return;
    }

    const toRemove = Math.min(result.products.length, document.querySelectorAll('#product-grid .grid__item--skeleton').length);
    document.querySelectorAll('#product-grid .grid__item--skeleton').forEach((s, i) => {
      if (i < toRemove) s.remove();
    });

    state.allProducts = state.allProducts.concat(result.products);
    state.filters = result.filters;
    syncFiltersContext();
    renderCollectionGrid(result.products, false);
    insertInGridSection();
    hydrateInGridSection();

    state.pagination.cursor = result.pageInfo.endCursor;
    state.pagination.hasNextPage = result.pageInfo.hasNextPage;
    state.pagination.totalLoaded += result.products.length;

    setProductCount();

    if (state.pagination.hasNextPage) {
      generateSkeletonLoaders(config.productsPerPage || 12, false);
      setupIntersectionObserver();
    } else {
      document.querySelectorAll('#product-grid .grid__item--skeleton').forEach((s) => s.remove());
    }
  } catch (err) {
    console.error('Error loading more products:', err);
    if (state.pagination.hasNextPage) setupIntersectionObserver();
  }
  state.pagination.isLoading = false;
  if (typeof ReloadSmartWishlist == 'function') ReloadSmartWishlist()
}

/*
  ┌─────────────────────────────────────────────────────────────────────────┐
  │ Fetch and render UI                                                     │
  └─────────────────────────────────────────────────────────────────────────┘
*/
async function fetchAndRenderUI(invoker, filters = [], sortKey = null, reverse = false) {

  const cachedResult = hasActiveFilters() && !sortKey ? getCachedProducts() : null;
  let result;

  if (cachedResult) {
    result = cachedResult;
    if (result.products.length === 0) {
      state.allProducts = [];
      state.filters = result.filters;
      syncFiltersContext();
      setProductCount();
      renderNoProducts();
      return;
    }
  } else {
    try {
      result = await fetchCollectionData(null, filters, sortKey, reverse, invoker === 'pageload');
      if (result.products.length === 0) {
        state.allProducts = [];
        state.filters = result.filters;
        syncFiltersContext();
        setProductCount(false, 0);
        renderNoProducts();
        return;
      }
    } catch (err) {
      console.error('Error fetching collection:', err);
      renderNoProducts();
      return;
    }
  }

  state.allProducts = result.products;
  state.pagination.cursor = result.pageInfo.endCursor;
  state.pagination.hasNextPage = result.pageInfo.hasNextPage;
  state.pagination.totalLoaded = result.products.length;
  state.filters = result.filters;
  syncFiltersContext();

  if (!cachedResult) setCachedProducts(result);

  showProducts();

  const skeletons = document.querySelectorAll('#product-grid .grid__item--skeleton');
  // const removeCount = Math.min(result.products.length, skeletons.length);
  // skeletons.forEach((s, i) => { if (i < removeCount) s.remove(); });
  skeletons.forEach((s) => { s.remove(); });

  renderCollectionGrid(result.products, hasActiveFilters());
  renderFilters(result.filters);
  if (state.priceRange.currentMin != null && state.priceRange.currentMax != null) {
    globalPriceRangeState.setState({
      minValue:
        ((state.priceRange.currentMin - state.priceRange.min) /
          (state.priceRange.max - state.priceRange.min)) *
        100,
      maxValue:
        ((state.priceRange.currentMax - state.priceRange.min) /
          (state.priceRange.max - state.priceRange.min)) *
        100,
    });
  }
  insertInGridSection();
  hydrateInGridSection();

  if (state.pagination.hasNextPage) {
    generateSkeletonLoaders(config.productsPerPage || 12, false);
    setupIntersectionObserver();
  } else {
    document.querySelectorAll('#product-grid .grid__item--skeleton').forEach((s) => s.remove());
  }

  // If not initial load then run this
  if (invoker !== 'pageload') {
    requestIdleCallback(updateURLQueryParams, {timeout: 250});
  }

  setProductCount();

  document.dispatchEvent(new CustomEvent('collectionProductsLoaded', {
    detail: {
      products: result.products,
      filters: result.filters,
      collectionHandle: config.collectionHandle,
      hasMore: state.pagination.hasNextPage
    }
  }));
}

function hydrateInGridSection() {
  document.querySelectorAll('.in-grid-snippet').forEach(el => {
    const raw = el.getAttribute('data-section');
    if (!raw) return;
    const data = JSON.parse(raw);
    switch (data.type) {
      case 'in_grid_smart_filter_category':
        hydrateImageCardSection(el, data, 'filter.p.product_type');
        break;
      case 'in_grid_smart_filter_activity':
        hydrateImageCardSection(el, data, 'filter.p.m.neo.activity_fitler');
        break;
      case 'in_grid_smart_filter_innovation':
        hydrateImageCardSection(el, data, 'filter.p.m.neo.innovation_filter');
        break;
      case 'in_grid_smart_filter_price':
        hydrateInGridPriceSection(el, data);
        break;
      case 'in_grid_smart_filter_coverage':
        hydrateInGridCoverageSection(el, data);
        break;
      case 'in_grid_smart_filter_color':
        hydrateColorSection(el, data, 'filter.p.m.custom.colors_filter');
        break;
      case 'in_grid_smart_filter_size':
        hydrateSizeSection(el, data);
        break;
      case 'in_grid_smart_filter_goggle_straps':
        hydrateImageCardSection(el, data, 'filter.p.m.custom.straps_filter');
        break;
      case 'in_grid_choose_the_grid':
        hydrateChooseTheGrid(el, data)
        break;
      case 'in_grid_video':
        hydrateInGridVideo(el, data)
        break;
      case 'in_grid_featured_products':
        hydrateInGridFeaturedProducts(el, data)
        break;
      case 'in_grid_find_my_fit':
        hydrateInGridFindMyFitSection(el, data);
        break;
      case 'in_grid_image_comparison':
        hydrateInGridImageComparisonSection(el, data);
        break;
    }
  });
}

function hydrateInGridFeaturedProducts(el, data) {
  const products = data.products || [];
  let buttonData = null;
  try {
    buttonData = JSON.parse(data.button || '{}');
  } catch { }
  el.innerHTML = `
    <section class="in-grid-featured-section">

      <picture>
        <source media="(max-width: 767px)"
          srcset="${data.background_image_mobile || data.background_image_desktop}">
        <img
          src="${data.background_image_desktop}"
          class="in-grid-featured-bg"
          loading="lazy"
        >
      </picture>

      <div class="in-grid-featured-content">

        ${data.heading ? `<h2 class="in-grid-featured-heading">${data.heading}</h2>` : ''}

        <div class="in-grid-featured-grid in-grid-card-slider hide-scrollbar"></div>

        ${buttonData?.url ? `
          <a href="${buttonData.url}"
             class="in-grid-banner-btn in-grid-featured-product-btn"
             style="background:${data.button_color};
                    color:${data.button_text_color}">
            ${buttonData.text || 'Explore'}
            ${data.button_corner === 'true' ? cornerMarkup() : ''}
          </a>
        ` : ''}

      </div>

    </section>
  `;

  const grid = el.querySelector('.in-grid-featured-grid');

  products.forEach((product, i) => {
    const html = generateCardForInGridFeaturedProduct(product, i);
    const temp = document.createElement('div');
    temp.innerHTML = html.trim();

    const li = temp.firstElementChild;
    if (li) {
      li.style.flex = '0 0 auto'; // ensure horizontal scroll behavior
      grid.appendChild(li);
    }
  });
}

// Normalize video: accept full object { previewImage, mediaContentType, sources } or legacy URL string
function getVideoUrl(video) {
  if (!video) return '';
  if (typeof video === 'string') return video;
  const sources = video.sources;
  if (!sources?.length) return '';
  const mp4 = sources.find(s => s.format === 'mp4');
  return (mp4?.url || sources[0].url) || '';
}

function getVideoPoster(video) {
  if (!video || typeof video === 'string') return '';
  return video.previewImage?.src || '';
}

function hydrateInGridVideo(el, data) {
  const desktopVideo = data.video;
  const mobileVideo = data.video_mobile || desktopVideo;
  const videoObj = isMobile ? mobileVideo : desktopVideo;
  const videoUrl = getVideoUrl(videoObj);
  const posterUrl = getVideoPoster(videoObj);
  if (!videoUrl) return;

  const sourcesMarkup = (() => {
    if (typeof videoObj === 'string' || !videoObj?.sources?.length) {
      return `<source src="${videoUrl}" type="video/mp4">`;
    }
    const sources720Mp4 = videoObj.sources.filter(s => s.url && s.height === 720 && (s.format === 'mp4' || (s.mimeType || '').includes('mp4')));
    const src = sources720Mp4[0]?.url || videoObj.sources[0]?.url;
    const type = sources720Mp4[0]?.mimeType || 'video/mp4';
    return src ? `<source src="${src}" type="${type}">` : '';
  })();

  el.innerHTML = `
    <section class="in-grid-video-section">
      <div class="in-grid-video-wrapper">
        <video class="in-grid-video" controlslist="nodownload" muted loop playsinline preload="metadata" ${posterUrl ? ` poster="${posterUrl}"` : ''}>
          ${sourcesMarkup}
        </video>

        <button
          class="in-grid-video-play glass-effect"
          aria-label="Play video">
          ${playIconSVG()}
        </button>
      </div>
    </section>
  `;

  const wrapper = el.querySelector('.in-grid-video-wrapper');
  const inGridVideo = wrapper.querySelector('.in-grid-video');
  const btn = wrapper.querySelector('.in-grid-video-play');

  btn.addEventListener('click', () => {
    if (inGridVideo.paused) {
      inGridVideo.play();
      inGridVideo.muted = false;
      btn.innerHTML = pauseIcon();
    } else {
      inGridVideo.pause();
      inGridVideo.muted = true;
      btn.innerHTML = playIconSVG();
    }
  });
}

function playIconSVG() {
  return `
    <svg width="22" height="25" viewBox="0 0 22 25" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M21 10.6625C22.3333 11.4323 22.3333 13.3568 21 14.1266L3 24.5189C1.66666 25.2887 6.86406e-07 24.3264 7.53704e-07 22.7868L1.66223e-06 2.00222C1.72953e-06 0.462624 1.66667 -0.499625 3 0.270175L21 10.6625Z" fill="white"/>
    </svg>
  `;
}

function pauseIcon() {
  return `❚❚`;
}

function hydrateChooseTheGrid(el, data) {

  const desktopImages = data.desktop_images || [];
  const mobileImages = data.mobile_images || [];

  let links = [];
  try {
    links = JSON.parse(data.links || '[]');
  } catch {
    links = [];
  }

  el.innerHTML = `
    <section class="in-grid-choose-gear">

      <div class="w-full flex items-center justify-between cornor-holder page-width in-grid-choose-gear-corner-container">
        <div>
          <svg xmlns="http://www.w3.org/2000/svg" width="38" height="38" viewBox="0 0 38 38" fill="none" style="transform: rotate(0deg);">
            <mask id="path-1-inside-1_6289_27722" fill="white">
            <path d="M0 0H38V38H0V0Z"></path>
            </mask>
            <path d="M0 0V-1H-1V0H0ZM0 0V1H38V0V-1H0V0ZM0 38H1V0H0H-1V38H0Z" fill="#EE3224" mask="url(#path-1-inside-1_6289_27722)"></path>
          </svg>
        </div>
        <div class="cornor-right">
          <svg xmlns="http://www.w3.org/2000/svg" width="38" height="38" viewBox="0 0 38 38" fill="none" style="transform: rotate(0deg);">
            <mask id="path-1-inside-1_6289_27722" fill="white">
            <path d="M0 0H38V38H0V0Z"></path>
            </mask>
            <path d="M0 0V-1H-1V0H0ZM0 0V1H38V0V-1H0V0ZM0 38H1V0H0H-1V38H0Z" fill="#EE3224" mask="url(#path-1-inside-1_6289_27722)"></path>
          </svg>
        </div>
      </div>

      ${data.heading ? `<h2 class="in-grid-choose-heading">${data.heading}</h2>` : ''}

      <div class="in-grid-choose-slider hide-scrollbar"></div>

      ${data.button ? `
        <div class="in-grid-choose-btn-wrapper">
          <a href="${data.button}"
            class="in-grid-banner-btn"
            style="background:${data.button_color};
                    color:${data.button_text_color}">
            Explore

            ${data.button_corners === 'true' ? cornerMarkup() : ''}
          </a>
        </div>
      ` : ''}

    </section>
  `;

  const slider = el.querySelector('.in-grid-choose-slider');

  const totalSlides = Math.max(desktopImages.length, mobileImages.length);

  for (let i = 0; i < totalSlides; i++) {

    const desktopImg = desktopImages[i];
    const mobileImg = mobileImages[i] || desktopImg;
    const link = links[i] || '#';

    if (!desktopImg && !mobileImg) continue;

    const slide = document.createElement('a');
    slide.className = 'in-grid-choose-card';
    slide.href = link;

    slide.innerHTML = `
      <picture>
        <source media="(max-width: 767px)" srcset="${mobileImg}">
        <img src="${desktopImg}" loading="lazy" alt="">
      </picture>
    `;

    slider.appendChild(slide);
  }
}

function hydrateImageCardSection(el, data, filterId) {
  const filter = state.filters.find(f => f.id === filterId);
  const activeValuesForFilter = Array.isArray(state.activeFilters[filterId])
    ? state.activeFilters[filterId]
    : [];
  if (!filter) return;

  let btnLink = null;
  let btnText = 'Explore';

  try {
    const parsed = JSON.parse(data.button_link || '{}');
    btnLink = parsed.url;
    btnText = parsed.text || btnText;
  } catch { }

  el.innerHTML = `
    <section class="in-grid-banner">

      <div class="in-grid-banner-wrapper">
        <picture>
          <source media="(max-width: 999px)"
            srcset="${data.background_image_mobile || data.background_image_desktop}">
          <img src="${data.background_image_desktop}"
            class="in-grid-banner-image"
            loading="lazy">
        </picture>

        <div class="in-grid-banner-content">

          ${data.heading ? `<h2>${data.heading}</h2>` : ''}

          <div class="in-grid-card-slider hide-scrollbar"></div>

          ${btnLink ? `
            <a href="${btnLink}"
              class="in-grid-banner-btn"
              style="background:${data.button_background_color};
                     color:${data.button_text_color}">
              ${btnText}
              ${data.button_corners === 'true' ? cornerMarkup() : ''}
            </a>
          ` : ''}

        </div>
      </div>

    </section>
  `;

  const slider = el.querySelector('.in-grid-card-slider');
  const validValues = filter.values; // only available .slice(0, 10);            // only first 10

  validValues.forEach(val => {
    const isActive = activeValuesForFilter.includes(val.label);
    const imgSrc = getFilterImageUrl(filter.label, val.label);

    const descKey =
      `${filter.label.toLowerCase().trim()}:${val.label.toLowerCase().trim()}`;

    const description = state.filterDescriptions[descKey] || '';

    const isDisabled = val.count === 0;

    const card = document.createElement('div');
    card.className = `in-grid-filter-card ${isActive ? 'active' : ''}`;

    card.style.opacity = isDisabled ? '0.5' : '1';
    card.style.pointerEvents = isDisabled ? 'none' : 'auto';

    const encodedFilterLabel = encodeURIComponent(filter.label);
    const encodedValueLabel = encodeURIComponent(val.label);
    card.innerHTML = `
    <img src="${imgSrc}" alt="${val.label}" data-filter-label="${encodedFilterLabel}" data-value-label="${encodedValueLabel}" data-image-attempt="0" onerror="handleFilterImageError(this)">
    <h4>${val.label}</h4>
    ${description ? `<p>${description}</p>` : ''}
  `;

    if (!isDisabled) {
      card.onclick = () => {
        if (!state.activeFilters[filterId]) {
          state.activeFilters[filterId] = [];
        }

        const idx = state.activeFilters[filterId].indexOf(val.label);

        if (idx === -1) {
          state.activeFilters[filterId].push(val.label);
        } else {
          state.activeFilters[filterId].splice(idx, 1);
        }

        if (state.activeFilters[filterId].length === 0) {
          delete state.activeFilters[filterId];
        }

        applyFilters();
      };
    }

    slider.appendChild(card);
  });
}

function hydrateInGridPriceSection(el, data) {
  if (!state.priceRange || state.priceRange.min == null || state.priceRange.max == null) return;

  const baseMin = state.priceRange.min;
  const baseMax = state.priceRange.max;
  const currentMin = state.priceRange.currentMin != null ? state.priceRange.currentMin : baseMin;
  const currentMax = state.priceRange.currentMax != null ? state.priceRange.currentMax : baseMax;

  let btnLink = null;
  let btnText = 'Shop';

  try {
    const parsed = JSON.parse(data.button_link || '{}');
    btnLink = parsed.url;
    btnText = parsed.text || btnText;
  } catch { }

  el.innerHTML = `
    <section class="in-grid-banner">
      <div class="in-grid-banner-wrapper">
        <picture>
          <source media="(max-width: 999px)"
            srcset="${data.background_image_mobile || data.background_image_desktop}">
          <img src="${data.background_image_desktop}"
            class="in-grid-banner-image"
            loading="lazy">
        </picture>
        <div class="in-grid-banner-content">
          ${data.heading ? `<h2>${data.heading}</h2>` : ''}
          <neo-price-range
            min="${baseMin}"
            max="${baseMax}"
            min-value="${currentMin}"
            max-value="${currentMax}">
          </neo-price-range>
          ${btnLink ? `
            <a href="${btnLink}"
              class="in-grid-banner-btn"
              style="background:${data.button_background_color};
                     color:${data.button_text_color}">
              ${btnText}
              ${data.button_corners === 'true' ? cornerMarkup() : ''}
            </a>
          ` : ''}
        </div>
      </div>
    </section>
  `;
}

function hydrateInGridCoverageSection(el, data) {
  const filterId = 'filter.p.m.neo.coverage_filter';
  const filter = state.filters.find(f => f.id === filterId);
  if (!filter) return;

  const currentGender = state.coverageGender || 'Women';

  const genderToggleHtml = `
    <div class="neo-coverage-gender-toggle">
      <button type="button" class="neo-coverage-gender-btn ${currentGender === 'Men' ? 'active' : ''}" data-gender="Men">
        Men
      </button>
      <button type="button" class="neo-coverage-gender-btn ${currentGender === 'Women' ? 'active' : ''}" data-gender="Women">
        Women
      </button>
    </div>
  `;

  const valuesHtml = (filter.values || []).map((value) => {
    const active = state.activeFilters[filter.id] && state.activeFilters[filter.id].includes(value.label);
    const disabled = value.count === 0;
    const activeClass = active ? 'active' : '';
    const disabledClass = disabled ? 'disabled' : '';
    const borderColor = COVERAGE_OPTION_COLORS[value.label] || '#FFFFFF';

    const descriptionKey = `${filter.label.toLowerCase().trim()}:${value.label.toLowerCase().trim()}`;
    const description = state.filterDescriptions[descriptionKey] || '';

    return `
      <div class="neo-filters-item neo-filters-coverage-item ${activeClass} ${disabledClass}" data-filter-id="${filter.id}" data-filter-value="${value.label}" ${disabled ? 'data-disabled="true"' : ''} role="button" tabindex="0">
        <div class="neo-coverage-checkbox" style="border-color: ${borderColor};">
          ${active ? `<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 3L4.5 8.5L2 6" stroke="${borderColor}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>` : ''}
        </div>
        <div style="flex: 1;">
          <p class="neo-filters-flex-item-text">${value.label}</p>
          ${description ? `<p class="neo-filters-flex-item-description line-clamp-1">${description}</p>` : ''}
        </div>
      </div>
    `;
  }).join('');

  let btnLink = null;
  let btnText = 'Shop';

  try {
    const parsed = JSON.parse(data.button_link || '{}');
    btnLink = parsed.url;
    btnText = parsed.text || btnText;
  } catch { }

  el.innerHTML = `
    <section class="in-grid-banner">

      <div class="in-grid-banner-wrapper">
        <picture>
          <source media="(max-width: 999px)"
            srcset="${data.background_image_mobile || data.background_image_desktop}">
          <img src="${data.background_image_desktop}"
            class="in-grid-banner-image"
            loading="lazy">
        </picture>

        <div class="in-grid-banner-content">

          ${data.heading ? `<h2>${data.heading}</h2>` : ''}

          <div class="in-grid-coverage-wrapper">
            ${genderToggleHtml}
            <div class="neo-coverage-layout">
              <div class="neo-coverage-visual">
                <img class="neo-coverage-dynamic-image"
                  src="${getCoverageGenderImage()}"
                  alt="Coverage levels diagram"
                  loading="lazy" />
              </div>
              <div class="neo-coverage-options neo-filters-flex neo-filters-coverage hide-scrollbar">
                ${valuesHtml}
              </div>
            </div>
          </div>

          ${btnLink ? `
            <a href="${btnLink}"
              class="in-grid-banner-btn"
              style="background:${data.button_background_color};
                     color:${data.button_text_color}">
              ${btnText}
              ${data.button_corners === 'true' ? cornerMarkup() : ''}
            </a>
          ` : ''}

        </div>
      </div>

    </section>
  `;

  initInGridCoverageClicks(el);
}

/** Coverage option checkbox border colors (used in sidebar and in-grid). */
const COVERAGE_OPTION_COLORS = {
  'High Leg': '#FFD700',
  'Medium Leg': '#00FF00',
  'Low Leg': '#800080',
  'Boy Leg': '#FFA500',
  'Sport Short': '#ADD8E6',
  'Bike Short': '#0000FF',
  'Jammer': '#4B0082'
};

function getCoverageCheckmarkSvg(borderColor) {
  return `<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 3L4.5 8.5L2 6" stroke="${borderColor}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`;
}

/**
 * In-grid coverage section is rendered after initFilterValueClicks runs,
 * so we attach coverage + gender click handlers here via delegation.
 */
function initInGridCoverageClicks(container) {
  container.addEventListener('click', (e) => {
    const coverageItem = e.target.closest('.neo-filters-coverage-item');
    if (coverageItem) {
      e.preventDefault();
      e.stopPropagation();
      handleInGridCoverageOptionClick(coverageItem);
      return;
    }

    const genderBtn = e.target.closest('.neo-coverage-gender-btn');
    if (genderBtn) {
      e.preventDefault();
      e.stopPropagation();
      handleInGridCoverageGenderClick(genderBtn);
    }
  });
}

function handleInGridCoverageOptionClick(item) {
  if (item.getAttribute('data-disabled') === 'true') return;

  const filterId = item.getAttribute('data-filter-id');
  const filterValue = item.getAttribute('data-filter-value');
  if (!filterId || !filterValue) return;

  if (!state.activeFilters[filterId]) state.activeFilters[filterId] = [];
  const idx = state.activeFilters[filterId].indexOf(filterValue);
  const checkbox = item.querySelector('.neo-coverage-checkbox');
  const borderColor = COVERAGE_OPTION_COLORS[filterValue] || '#FFFFFF';

  if (idx === -1) {
    state.activeFilters[filterId].push(filterValue);
    item.classList.add('active');
    if (checkbox) checkbox.innerHTML = getCoverageCheckmarkSvg(borderColor);
  } else {
    state.activeFilters[filterId].splice(idx, 1);
    item.classList.remove('active');
    if (checkbox) checkbox.innerHTML = '';
  }

  if (state.activeFilters[filterId].length === 0) delete state.activeFilters[filterId];
  applyFilters();
}

function handleInGridCoverageGenderClick(btn) {
  const gender = btn.getAttribute('data-gender');
  if (!gender) return;

  state.coverageGender = gender;

  document.querySelectorAll('.neo-coverage-dynamic-image').forEach((img) => {
    img.src = getCoverageGenderImage();
  });

  const genderFilterId = 'filter.p.m.custom.gender_filter';
  state.activeFilters[genderFilterId] = [gender];

  const wrapper = document.querySelector('.neo-collection-wrapper');
  if (wrapper) {
    wrapper.querySelectorAll('.neo-coverage-gender-btn').forEach((b) => b.classList.remove('active'));
    wrapper.querySelectorAll(`.neo-filters-item[data-filter-id="${genderFilterId}"]`).forEach((item) => {
      const value = item.getAttribute('data-filter-value');
      item.classList.toggle('active', value === gender);
    });
  }
  btn.classList.add('active');
  applyFilters();
}

function hydrateInGridFindMyFitSection(el, data) {
  const desktopImg = data.background_image_desktop;
  const mobileImg = data.background_image_mobile || desktopImg;
  const heading = data.heading || '';
  const subHeading = data.sub_heading || '';
  const btnHref = data.button || '';
  const btnBg = data.button_background_color || '#000000';
  const btnColor = data.button_text_color || '#ffffff';
  const showCorners = data.button_corners === 'true';

  el.innerHTML = `
    <section class="in-grid-find-my-fit">
    <div class="tag-absolute-wrapper">
      <div class="tag-block w-fit" style="font-size: 12px;">
        <span class="tag-block__text font-athletics-medium">FIT AND COMFOT GUIDE</span>
      </div>
    </div>
      <div class="in-grid-banner-wrapper">
        <picture>
          <source media="(max-width: 999px)"
            srcset="${mobileImg}">
          <img src="${desktopImg}"
            class="in-grid-banner-image"
            loading="lazy">
        </picture>

        <div class="in-grid-banner-content">

          ${heading ? `<h2>${heading}</h2>` : ''}
          ${subHeading ? `<p class="in-grid-find-my-fit-subheading">${subHeading}</p>` : ''}

          ${btnHref ? `
            <a href="${btnHref}"
              class="in-grid-banner-btn"
              style="background:${btnBg};
                     color:${btnColor}">
              Shop
              ${showCorners ? cornerMarkup() : ''}
            </a>
          ` : ''}

        </div>
      </div>

    </section>
  `;
}

function hydrateInGridImageComparisonSection(el, data) {
  const leftRaw = data.data?.collection_on_left || data.collection_on_left || [];
  const rightRaw = data.data?.collections_on_right || data.collections_on_right || [];

  if (!leftRaw.length || !rightRaw.length) {
    el.innerHTML = '';
    return;
  }

  const normalizeCollection = (col) => {
    const title = col.collection_compare_title?.value || col.title || '';
    const imgData = col.collection_comapre_image || {};

    let tabs = [];
    let images = [];
    let handle = col.handle || '';

    if (Array.isArray(imgData)) {
      images = imgData.map((src) => ({
        src,
        alt: title,
      }));
    } else {
      const labels = Object.keys(imgData);
      tabs = labels;
      images = labels.map((label) => ({
        src: imgData[label],
        alt: title || label,
      }));
    }

    if (!tabs.length) {
      tabs = images.map((_, idx) => `Option ${idx + 1}`);
    }

    return {
      id: col.id,
      title,
      tabs,
      images,
      handle: col.handle || '',
    };
  };

  const leftCollections = leftRaw.map(normalizeCollection);
  const rightCollections = rightRaw.map(normalizeCollection);

  if (!leftCollections.length || !rightCollections.length) {
    el.innerHTML = '';
    return;
  }

  const initialLeft = leftCollections[0];
  const initialRight = rightCollections[0];
  const initialLeftUrl = initialLeft?.handle ? `/collections/${initialLeft.handle}` : '#';
  const initialRightUrl = initialRight?.handle ? `/collections/${initialRight.handle}` : '#';

  el.innerHTML = `
    <section class="in-grid-image-comparison">
      <div class="igcc-wrapper">
        <div class="igcc-header">
          <div class="igcc-dropdown igcc-dropdown--left">
            <button class="igcc-dropdown__selected" type="button" aria-haspopup="listbox" aria-expanded="false">
              <span class="igcc-dropdown__label line-clamp"></span>
              <span class="igcc-dropdown__chevron">
                <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1 1L6 7L11 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
            </button>
            <ul class="igcc-dropdown__list" role="listbox"></ul>
          </div>
          <span class="igcc-vs">vs</span>
          <div class="igcc-dropdown igcc-dropdown--right">
            <button class="igcc-dropdown__selected" type="button" aria-haspopup="listbox" aria-expanded="false">
              <span class="igcc-dropdown__label line-clamp"></span>
              <span class="igcc-dropdown__chevron">
                <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1 1L6 7L11 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
            </button>
            <ul class="igcc-dropdown__list" role="listbox"></ul>
          </div>
        </div>

        <div class="igcc-slider-wrapper">
          <comparison-slider-component data-initial-position="50">
            <div class="ic-root">
              <a class="ic-link--left" href="${initialLeftUrl}">
                <div class="ic-clip--left">
                  <img class="ic-img--left" src="" alt="" loading="lazy" />
                </div>
              </a>
              <a class="ic-link--right" href="${initialRightUrl}">
                <img class="ic-img--right" src="" alt="" loading="lazy" />
              </a>
              <div class="ic-divider" style="left:50%;">
                <div class="ic-line"></div>
                <div class="ic-handle" role="slider" aria-label="Image comparison" aria-valuemin="0" aria-valuemax="100" aria-valuenow="50" tabindex="0">
                  <svg width="45" height="45" viewBox="0 0 45 45" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" class="__web-inspector-hide-shortcut__">
                  <circle cx="22.1188" cy="22.1186" r="20.9547" fill="white" stroke="#EE3224" stroke-width="2.3283"></circle>
                  <path d="M35.2985 21.3055C35.739 21.7575 35.739 22.4782 35.2985 22.9303L30.23 28.1326C29.5011 28.8807 28.232 28.3647 28.232 27.3202L28.232 16.9155C28.232 15.8711 29.5011 15.355 30.23 16.1031L35.2985 21.3055Z" fill="#EE3224"></path>
                  <path d="M8.94011 21.3055C8.49969 21.7575 8.49969 22.4782 8.94011 22.9303L14.0086 28.1326C14.7375 28.8807 16.0066 28.3647 16.0066 27.3202L16.0066 16.9155C16.0066 15.8711 14.7375 15.355 14.0086 16.1031L8.94011 21.3055Z" fill="#EE3224"></path>
                </svg>
                </div>
              </div>
            </div>
          </comparison-slider-component>
        </div>

        <div class="igcc-tabs" role="tablist"></div>
      </div>
    </section>
  `;

  const wrapper = el.querySelector('.igcc-wrapper');
  if (!wrapper) return;

  const state = {
    leftIndex: 0,
    rightIndex: 0,
    tabIndex: 0,
  };

  const dropdownLeft = wrapper.querySelector('.igcc-dropdown--left');
  const dropdownRight = wrapper.querySelector('.igcc-dropdown--right');
  const tabsEl = wrapper.querySelector('.igcc-tabs');
  const sliderEl = wrapper.querySelector('comparison-slider-component');


  function disabledSameDropdown() {
    const leftId = leftCollections[state.leftIndex]?.id;
    const rightId = rightCollections[state.rightIndex]?.id;

    const setDisabledForList = (listEl, collections, idToDisable) => {
      if (!listEl) return;

      listEl.querySelectorAll('.igcc-dropdown__item').forEach((item) => {
        const idx = Number(item.dataset.index);
        const colId = collections[idx]?.id;
        const isDisabled = idToDisable != null && colId === idToDisable;

        item.classList.toggle('is-disabled', isDisabled);
        item.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');

        item.style.pointerEvents = isDisabled ? 'none' : '';
        item.style.opacity = isDisabled ? '0.5' : '';
        item.style.cursor = isDisabled ? 'not-allowed' : '';
      });
    };

    setDisabledForList(rightControls?.listEl, rightCollections, leftId);
    setDisabledForList(leftControls?.listEl, leftCollections, rightId);
  }

  function buildDropdown(dropdownEl, collections, side) {
    const button = dropdownEl.querySelector('.igcc-dropdown__selected');
    const labelEl = dropdownEl.querySelector('.igcc-dropdown__label');
    const listEl = dropdownEl.querySelector('.igcc-dropdown__list');

    collections.forEach((col, idx) => {
      const item = document.createElement('li');
      item.className = 'igcc-dropdown__item';
      item.classList.add('line-clamp');
      item.setAttribute('role', 'option');
      item.dataset.index = String(idx);
      item.textContent = col.title;
      item.addEventListener('click', () => {
        if (side === 'left') state.leftIndex = idx;
        if (side === 'right') state.rightIndex = idx;
        state.tabIndex = 0;
        closeDropdown(dropdownEl, button);
        updateUI();
        const compObject = document.querySelector('.in-grid-image-comparison')?.querySelector('comparison-slider-component');
        if (compObject && typeof compObject._applyPosition === 'function') compObject._applyPosition(50);
      });
      listEl.appendChild(item);
    });

    button.addEventListener('click', (e) => {
      e.stopPropagation();
      toggleDropdown(dropdownEl, button);
    });

    document.addEventListener('click', () => {
      closeDropdown(dropdownEl, button);
    });

    return { labelEl, listEl };
  }

  function toggleDropdown(wrapperEl, btn) {
    const isOpen = wrapperEl.classList.contains('is-open');
    wrapper.querySelectorAll('.igcc-dropdown').forEach(d => d.classList.remove('is-open'));
    wrapper.querySelectorAll('.igcc-dropdown__selected').forEach(b => b.setAttribute('aria-expanded', 'false'));
    if (!isOpen) {
      wrapperEl.classList.add('is-open');
      btn.setAttribute('aria-expanded', 'true');
    }
  }

  function closeDropdown(wrapperEl, btn) {
    wrapperEl.classList.remove('is-open');
    btn.setAttribute('aria-expanded', 'false');
  }

  const leftControls = buildDropdown(dropdownLeft, leftCollections, 'left');
  const rightControls = buildDropdown(dropdownRight, rightCollections, 'right');

  function updateDropdownLabels() {
    const lc = leftCollections[state.leftIndex];
    const rc = rightCollections[state.rightIndex];

    if (leftControls.labelEl) leftControls.labelEl.textContent = lc.title;
    if (rightControls.labelEl) rightControls.labelEl.textContent = rc.title;

    leftControls.listEl.querySelectorAll('.igcc-dropdown__item').forEach((item, i) => {
      item.classList.toggle('is-selected', i === state.leftIndex);
      item.setAttribute('aria-selected', i === state.leftIndex ? 'true' : 'false');
      item.classList.add('line-clamp');
    });
    rightControls.listEl.querySelectorAll('.igcc-dropdown__item').forEach((item, i) => {
      item.classList.toggle('is-selected', i === state.rightIndex);
      item.setAttribute('aria-selected', i === state.rightIndex ? 'true' : 'false');
      item.classList.add('line-clamp');
    });
  }

  function updateTabs() {
    const lc = leftCollections[state.leftIndex];
    const labels = lc.tabs || [];

    tabsEl.innerHTML = '';
    labels.forEach((label, i) => {
      const btn = document.createElement('button');
      btn.className = 'igcc-tab';
      btn.setAttribute('role', 'tab');
      btn.setAttribute('aria-selected', i === state.tabIndex ? 'true' : 'false');
      btn.textContent = label;
      btn.addEventListener('click', () => {
        state.tabIndex = i;
        updateSliderImages();
        updateTabActiveState();
        // Reset the comparison slider back to the middle on each tab change.
        const compObject = document.querySelector('.in-grid-image-comparison')?.querySelector('comparison-slider-component');
        if (compObject && typeof compObject._applyPosition === 'function') compObject._applyPosition(50);
      });
      tabsEl.appendChild(btn);
    });

    updateTabActiveState();
  }

  function updateTabActiveState() {
    tabsEl.querySelectorAll('.igcc-tab').forEach((btn, i) => {
      btn.classList.toggle('is-active', i === state.tabIndex);
      btn.setAttribute('aria-selected', i === state.tabIndex ? 'true' : 'false');
    });
  }

  function updateSliderImages() {
    const lc = leftCollections[state.leftIndex];
    const rc = rightCollections[state.rightIndex];
    if (!lc || !rc) return;

    const leftUrl = lc.handle ? `/collections/${lc.handle}` : '#';
    const rightUrl = rc.handle ? `/collections/${rc.handle}` : '#';

    const setCollectionLinks = () => {
      if (!sliderEl) return;
      const linkL = sliderEl.querySelector('.ic-link--left');
      const linkR = sliderEl.querySelector('.ic-link--right');
      if (linkL) linkL.href = leftUrl;
      if (linkR) linkR.href = rightUrl;
    };

    const imgL = lc.images[state.tabIndex] || lc.images[0];
    const imgR = rc.images[state.tabIndex] || rc.images[0];
    if (!imgL || !imgR) {
      setCollectionLinks();
      return;
    }

    const doUpdate = () => {
      if (sliderEl && typeof sliderEl.updateImages === 'function') {
        sliderEl.updateImages({
          'slide-1': { src: imgL.src, alt: imgL.alt || lc.title, link: leftUrl },
          'slide-2': { src: imgR.src, alt: imgR.alt || rc.title, link: rightUrl },
        });
      } else if (sliderEl) {
        const iL = sliderEl.querySelector('.ic-img--left');
        const iR = sliderEl.querySelector('.ic-img--right');
        if (iL) { iL.src = imgL.src; iL.alt = imgL.alt || lc.title; }
        if (iR) { iR.src = imgR.src; iR.alt = imgR.alt || rc.title; }
      }
      setCollectionLinks();
    };

    if (sliderEl) {
      doUpdate();
    } else {
      requestAnimationFrame(doUpdate);
    }
  }

  function updateUI() {
    updateDropdownLabels();
    disabledSameDropdown();
    updateTabs();
    updateSliderImages();
  }

  updateUI();

  // Re-sync after custom element upgrade (updateImages can run before connectedCallback).
  if (typeof customElements !== 'undefined') {
    customElements.whenDefined('comparison-slider-component').then(() => {
      updateSliderImages();
    });
  }
}

function hydrateColorSection(el, data, filterId) {
  const filter = state.filters.find(f => f.id === filterId);
  if (!filter) return;
  let btnLink = null;
  let btnText = 'Find My Size';

  try {
    const parsed = JSON.parse(data.button_link || '{}');
    btnLink = parsed.url;
    btnText = parsed.text || btnText;
  } catch { }

  el.innerHTML = `
    <section class="in-grid-banner in-grid-color">

      <div class="in-grid-banner-wrapper">
        <picture>
          <source media="(max-width: 999px)"
            srcset="${data.background_image_mobile || data.background_image_desktop}">
          <img src="${data.background_image_desktop}"
            class="in-grid-banner-image"
            loading="lazy">
        </picture>

        <div class="in-grid-banner-content">

          ${data.heading ? `<h2>${data.heading}</h2>` : ''}

          <div class="in-grid-card-slider in-grid-color-slider hide-scrollbar"></div>
          ${btnLink ? `
            <a href="${btnLink}"
              class="in-grid-banner-btn"
              style="background:${data.button_background_color};
                     color:${data.button_text_color}">
              ${btnText}

              ${data.button_corners === 'true' ? `
                <span class="c1">${cornerSVG()}</span>
                <span class="c2">${cornerSVG()}</span>
                <span class="c3">${cornerSVG()}</span>
                <span class="c4">${cornerSVG()}</span>
              ` : ''}
            </a>
          ` : ''}

        </div>
      </div>

    </section>
  `;

  const slider = el.querySelector('.in-grid-color-slider');

  filter.values
    .slice(0, 10)
    .forEach(val => {

      const img = getFilterImageUrl(filter.label, val.label);
      const isDisabled = val.count === 0;

      const swatch = document.createElement('div');
      swatch.className = 'in-grid-color-swatch';

      if (isDisabled) {
        swatch.classList.add('disabled');
      }

      const encodedFilterLabel = encodeURIComponent(filter.label);
      const encodedValueLabel = encodeURIComponent(val.label);
      swatch.innerHTML = `<img src="${img}" alt="${val.label}" data-filter-label="${encodedFilterLabel}" data-value-label="${encodedValueLabel}" data-image-attempt="0" onerror="handleFilterImageError(this)">`;

      if (!isDisabled) {
        swatch.onclick = () => applyInGridFilter(filterId, val.label);
      }

      slider.appendChild(swatch);
    });
}

function hydrateSizeSection(el, data) {
  const filterId = 'filter.v.option.size';
  const activeValuesForFilter = Array.isArray(state.activeFilters[filterId]) ? state.activeFilters[filterId] : [];
  const filter = state.filters.find(f => f.id === filterId);
  if (!filter) return;

  let btnLink = null;
  let btnText = 'Find My Size';

  try {
    const parsed = JSON.parse(data.button_link || '{}');
    btnLink = parsed.url;
    btnText = parsed.text || btnText;
  } catch { }

  el.innerHTML = `
    <section class="in-grid-size">

      <div class="in-grid-banner-wrapper">
        <picture>
          <source media="(max-width: 999px)"
            srcset="${data.background_image_mobile || data.background_image_desktop}">
          <img src="${data.background_image_desktop}"
            class="in-grid-banner-image"
            loading="lazy">
        </picture>

        <div class="in-grid-banner-content">

          ${data.heading ? `<h2>${data.heading}</h2>` : ''}

          <div class="in-grid-card-slider in-grid-size-slider hide-scrollbar"></div>

          ${btnLink ? `
            <a href="${btnLink}"
              class="in-grid-banner-btn"
              style="background:${data.button_background_color};
                     color:${data.button_text_color}">
              ${btnText}

              ${data.button_corners === 'true' ? `
                <span class="c1">${cornerSVG()}</span>
                <span class="c2">${cornerSVG()}</span>
                <span class="c3">${cornerSVG()}</span>
                <span class="c4">${cornerSVG()}</span>
              ` : ''}
            </a>
          ` : ''}

        </div>
      </div>

    </section>
  `;

  const slider = el.querySelector('.in-grid-size-slider');

  filter.values
    .slice(0, 10)
    .forEach(val => {
      const isDisabled = val.count === 0;
      const isActive = activeValuesForFilter.includes(val.label)
      const btn = document.createElement('button');
      btn.className = 'in-grid-size-btn line-clamp-1';
      if (isDisabled) {
        btn.classList.add('disabled');
      }
      if (isActive) {
        btn.classList.add('active');
      }
      btn.innerHTML = `<span class="neo-filters-clear-filter-icon glass-effect relative">
          <svg width="6" height="6" viewBox="0 0 6 6" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0.707031 -3.09053e-08L2.85352 2.14648L5 -2.18557e-07L5.70703 0.707031L3.56055 2.85352L5.70703 5L5 5.70703L2.85352 3.56055L0.707031 5.70703L-3.09035e-08 5L2.14648 2.85352L-2.18557e-07 0.707031L0.707031 -3.09053e-08Z" fill="white"></path>
          </svg>
        </span>
        <p class="neo-filters-grid-item-text">${val.label}</p>`;

      if (!isDisabled) {
        btn.onclick = () => {
          if (!state.activeFilters[filterId]) {
            state.activeFilters[filterId] = [];
          }

          const idx = state.activeFilters[filterId].indexOf(val.label);

          if (idx === -1) {
            state.activeFilters[filterId].push(val.label);
          } else {
            // ✅ toggle like filters (important)
            state.activeFilters[filterId].splice(idx, 1);
          }

          if (state.activeFilters[filterId].length === 0) {
            delete state.activeFilters[filterId];
          }

          applyFilters();
        };
      }

      slider.appendChild(btn);
    });
}

function applyInGridFilter(filterId, value) {
  if (!state.activeFilters[filterId]) {
    state.activeFilters[filterId] = [];
  }

  if (!state.activeFilters[filterId].includes(value)) {
    state.activeFilters[filterId].push(value);
  }

  applyFilters();
}

function cornerMarkup() {
  return `
    <span class="c1">${cornerSVG()}</span>
    <span class="c2">${cornerSVG()}</span>
    <span class="c3">${cornerSVG()}</span>
    <span class="c4">${cornerSVG()}</span>
  `;
}

function renderButtonCorners() {
  return `
    <span class="corner c1">${cornerSVG()}</span>
    <span class="corner c2">${cornerSVG()}</span>
    <span class="corner c3">${cornerSVG()}</span>
    <span class="corner c4">${cornerSVG()}</span>
  `;
}

function cornerSVG() {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none">
    <mask id="path-1-inside-1_6289_29312" fill="white">
    <path d="M0 2C0 0.895431 0.895431 0 2 0H6C7.10457 0 8 0.895431 8 2V6C8 7.10457 7.10457 8 6 8H2C0.895431 8 0 7.10457 0 6V2Z"/>
    </mask>
    <path d="M-0.5 2C-0.5 0.619288 0.619288 -0.5 2 -0.5H5.5C6.88071 -0.5 8 0.619288 8 2C8 1.17157 7.10457 0.5 6 0.5H2C1.17157 0.5 0.5 1.17157 0.5 2H-0.5ZM8 8H0H8ZM2 8C0.619288 8 -0.5 6.88071 -0.5 5.5V2C-0.5 0.619288 0.619288 -0.5 2 -0.5V0.5C1.17157 0.5 0.5 1.17157 0.5 2V6C0.5 7.10457 1.17157 8 2 8ZM8 0V8V0Z" fill="black" mask="url(#path-1-inside-1_6289_29312)"/>
  </svg>`;
}

function insertInGridSection() {
  const grid = document.getElementById('product-grid');
  if (!grid) return;

  const productItems = [...grid.querySelectorAll('li.grid__item:not(.in-grid-section)')];
  const loadedCount = productItems.length;
  const perRow = getProductsPerRow();

  state.inGridSections.forEach(section => {

    if (state.insertedGridSections.has(section.id)) return;

    const rowPos = getSectionRowPosition(section);
    if (!rowPos || rowPos <= 0) return;

    const requiredProducts = rowPos * perRow;

    if (loadedCount < requiredProducts) return;

    const html = renderInGridSection(section);
    if (!html) return;

    const wrapper = document.createElement('div');
    wrapper.innerHTML = html.trim();
    const node = wrapper.firstElementChild;

    const insertIndex = requiredProducts;

    if (insertIndex >= productItems.length) {
      grid.appendChild(node);
    } else {
      grid.insertBefore(node, productItems[insertIndex]);
    }

    state.insertedGridSections.add(section.id);
  });
}

function renderInGridSection(section) {
  const template = document.querySelector(
    `#in-grid-snippet-templates [data-snippet-type="${section.type}"]`
  );

  if (!template) return '';

  const clone = template.cloneNode(true);

  // must live on snippet element itself
  clone.setAttribute('data-section', JSON.stringify(section));

  return `
    <li class="grid__item grid__item-full-width in-grid-section">
      ${clone.outerHTML}
    </li>
  `;
}

function getProductsPerRow() {
  const w = window.innerWidthCached;

  if (w < 600) return 1;
  if (w < 990) return 2;
  return 3;
}

function getSectionRowPosition(section) {
  const w = window.innerWidthCached;

  if (w < 600) return Number(section.section_position_mobile || 0);
  return Number(section.section_position_desktop || 0);
}

/*
  ┌─────────────────────────────────────────────────────────────────────────┐
  │ Init                                                                    │
  └─────────────────────────────────────────────────────────────────────────┘
*/

async function init() {
  if (!config.collectionHandle) {
    console.error('Neo collection: collection handle not configured');
    return;
  }

  const hasURLFilters = parseURLFilters();
  const graphqlFilters = hasURLFilters ? buildGraphQLFiltersFromActiveFilters() : [];

  if (hasActiveFilters()) {
    state.totalRenderedProducts = 0;
  }
  state.insertedGridSections.clear();

  await fetchAndRenderUI('pageload', graphqlFilters, state.currentSort.sortKey, state.currentSort.reverse);
  state.firstLoad = false;
}

init();

/*
  ┌─────────────────────────────────────────────────────────────────────────┐
  │ Sorting                                                                 │
  └─────────────────────────────────────────────────────────────────────────┘
*/
document.querySelectorAll('[data-sort-select]').forEach(dropdown => {
  const trigger = dropdown.querySelector('.custom-select-trigger')
  const optionsBox = dropdown.querySelector('.custom-options')
  const selectedText = dropdown.querySelector('.selected-value')

  trigger.addEventListener('click', () => {
    optionsBox.style.display = optionsBox.style.display === 'block' ? 'none' : 'block'
  })

  dropdown.querySelectorAll('.custom-option').forEach(option => {
    option.addEventListener('click', () => {
      const value = option.dataset.value
      selectedText.textContent = option.textContent
      const mapped = mapSortValue(value)
      state.currentSort.sortValue = value
      state.currentSort.sortKey = mapped.sortKey
      state.currentSort.reverse = mapped.reverse
      applyFilters()
    })
  })

  document.addEventListener('click', e => {
    if (!dropdown.contains(e.target)) {
      optionsBox.style.display = 'none'
    }
  })
})

function mapSortValue(sortValue) {
  switch (sortValue) {
    case 'manual':
      return { sortKey: 'MANUAL', reverse: false }

    case 'best-selling':
      return { sortKey: 'BEST_SELLING', reverse: false }

    case 'title-ascending':
      return { sortKey: 'TITLE', reverse: false }

    case 'title-descending':
      return { sortKey: 'TITLE', reverse: true }

    case 'price-ascending':
      return { sortKey: 'PRICE', reverse: false }

    case 'price-descending':
      return { sortKey: 'PRICE', reverse: true }

    case 'created-ascending':
      return { sortKey: 'CREATED', reverse: false }

    case 'created-descending':
      return { sortKey: 'CREATED', reverse: true }

    default:
      return { sortKey: null, reverse: false }
  }
}

function openFiltersModal() {
  const aside = document.querySelector('aside.neo-collection-aside');
  if (!aside) return;
  aside.classList.add('active');
  setTimeout(() => {
    document.body.classList.add('neo-filter-drawer-open');
  }, 0);
}

function clsoeMobileFilter() {
  const aside = document.querySelector('aside.neo-collection-aside');
  if (!aside) return;
  aside.classList.remove('active');
  setTimeout(() => {
    document.body.classList.remove('neo-filter-drawer-open');
  }, 0);
}
